/* eslint-disable react-hooks/set-state-in-render, react-hooks/refs --
 * The page-preset memo sets state while resolving a preset, and render reads a few refs. Both
 * patterns were ported as-is and need a behavior-preserving refactor of their own. */
import './SingleChannelPage.scss';
import * as R from 'ramda';
import {
  Dispatch,
  ReactElement,
  SetStateAction,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { MdInfoOutline, MdOutlineMenuBook, MdOutlineWarning } from 'react-icons/md';
import * as uuid from 'uuid';
import { PlatformOptions, SourceOptions } from '@blisspointmedia/bpm-types/dist/App2Performance';
import { GetPageDataResponse as ChannelPageDataResponse } from '@blisspointmedia/bpm-types/dist/Columns';
import { Metric, MetricTotalsByDate } from '@blisspointmedia/bpm-types/dist/CrossChannel';
import {
  GetCustomMetricsParams,
  GetCustomMetricsResponse
} from '@blisspointmedia/bpm-types/dist/CustomMetrics';
import { DateGroupingKey } from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import { formatPercent } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { AggregatorDatum } from '@blisspointmedia/bpm-types/dist/Metrics';
import {
  getMetricFormatter,
  METRIC_TO_FORMATTER,
  MetricFormatter
} from '@blisspointmedia/bpm-types/dist/metricFormatUtils';
import * as L from '@blisspointmedia/bpm-types/dist/LinearPerformance';
import {
  DEFAULT_LINEAR_AUDIENCE,
  LINEAR_AUDIENCES,
  LinearAudience
} from '@blisspointmedia/bpm-types/dist/LinearAudiences';
import {
  DEFAULT_LAG_ID,
  isQueryArgOnChannel,
  LagId
} from '@blisspointmedia/bpm-types/dist/QueryArgConfig';
import {
  DateInfo,
  DeletePresetParams as DeletePagePresetParams,
  GlobalOptions,
  MetricsPagePreset,
  SavePresetParams as SavePagePresetParams
} from '@blisspointmedia/bpm-types/dist/MetricsPage';
import {
  Column,
  DEFAULT_DATE_RANGE_STATE,
  DEFAULT_OTHER_DATE_RANGE_STATE,
  DeletePresetParams as DeleteTablePresetParams,
  DimensionColumn,
  GetPresetParams as GetTablePresetParams,
  LINEAR,
  MetricsTablePreset,
  PresetIDGroups,
  SavePresetParams as SaveTablePresetParams,
  SEARCHSHOPPING,
  STREAMING_PLUS,
  YOUTUBE
} from '@blisspointmedia/bpm-types/dist/MetricsTable';
import * as P from '@blisspointmedia/bpm-types/dist/Performance';
import { FilterPreset } from '@blisspointmedia/bpm-types/dist/pgTables/FilterPreset';
import { computeResolvedDate } from '@blisspointmedia/bpm-types/dist/RelativeDatePicker';
import * as S from '@blisspointmedia/bpm-types/dist/StreamingPerformance';
import { TEST_COMPANIES } from '@blisspointmedia/bpm-types/dist/TestCompanies';
import * as Y from '@blisspointmedia/bpm-types/dist/YoutubePerformance';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlertDialog } from '@Components/AlertDialog/AlertDialog';
import { DatePicker } from '@Components/DatePicker';
import { Background, Design, Intent, Size } from '@Components/designConstants';
import { DownloadDropdown } from '@Components/DownloadDropdown';
import { Dropdown } from '@Components/Dropdown';
import { Toolbar } from '@Components/Toolbar';
import { FilterBar } from '@Components/FilterBar';
import { getBasicTableFilterTerms } from '@Components/FilterBar/filterbar-utils';
import { GlossaryItem, GlossaryModal } from '@Components/GlossaryModal/GlossaryModal';
import { useKpiPickerData } from '@Components/KpiPicker/KpiPicker';
import { Page } from '@Components/Page';
import { PAGE_STATE } from '@Components/Page/page-constants';
import { SegmentedControlButton } from '@Components/SegmentedControlButton';
import { SparkChart } from '@Components/SparkChart/SparkChart';
import { FullPageSpinner } from '@Components/Spinner';
import { Button } from '@Components/Button';
import { CompareMetrics, CompareMetricOption } from '@Pages/CrossChannel/Components/CompareMetrics';
import { SourcePicker } from '@Pages/CrossChannel/Components/SourcePicker';
import { makePrettyDateRange, shouldShowTilde } from '@Pages/CrossChannel/cross-channel-utils';
import { Skeleton, TableSkeleton } from '@Components/Skeleton';
import {
  ALL_GROUP_NAME,
  getGlobalBrand,
  getLagPickerOptions,
  PerformanceContext,
  SingleChannelMediaTypes,
  STANDARD_GROUP_NAME
} from '@src/utils/performance-utils';
import { useAppSelector } from '@src/redux';
import { mediaTypesSelector } from '@src/redux/company';
import { useSetAreYouSure, useSetError } from '@src/redux/modals';
import * as UserRedux from '@src/redux/user';
import { useCompanyInfo } from '@src/utils/hooks/useCompanyInfo';
import {
  comparisonRangeBefore,
  DateRange,
  resolvePresetDateInfo,
  snapToMonday,
  validDate
} from '@src/utils/date-utils';
import { download } from '@src/utils/download-utils';
import { useExperimentFlag } from '@src/utils/experiment-context';
import {
  awaitJSON,
  LinearPerformanceLambdaFetch,
  SingleChannelLambdaFetch
} from '@src/utils/fetch-utils';
import { useMap } from '@src/utils/hooks/useData';
import { useExperimentalAnalyticsCols } from '@src/utils/hooks/useExperimentalAnalyticsCols';
import { useLocationCompany } from '@src/utils/hooks/useLocationCompany';
import { setPresetSearchParams } from '@src/utils/routing/preset-url-utils';
import { usePresetUrl } from '@src/utils/routing/usePresetUrl';
import { Mixpanel, MxE } from '@src/utils/mixpanelWrapper';
import { FetchFilterBar, useFilterPaneState } from '@Components/FetchFilterBar/FetchFilterBar';
import {
  CellData,
  ColumnMetaData,
  ColumnMetaDataMap,
  constructTableData,
  convertCustomMetric,
  convertKPIMapToOptions,
  createComparisonTablePreset,
  DEFAULT_GET_DIMENSION_CELL,
  DimensionMap,
  getDataHeaders,
  getDimensionDataHeaders,
  getExportedData,
  getRowAggregatorDatum,
  joinMetricsTableData,
  sortData,
  useGroupedSegmentFiltering
} from '@Components/MetricsTable/metrics-table-utils';
import { MetricsTableWithPresets as MetricsTable } from '@Components/MetricsTable/MetricsTableWithPresets';
import {
  isServerValueUsable,
  readServerValue,
  ServerMetricValues,
  ServerValueMap
} from '@Components/MetricsTable/server-metric-values';
import { MetricsTableHandle } from '@Components/MetricsTable/MetricsTable';
import {
  getInvalidAnalyticsDetails,
  extractDimsFromFilterPreset,
  getInvalidGranularityDetails,
  getInvalidGranularityDetailsFromPreset,
  InvalidGranularityDetails
} from '@src/utils/columnMetricSystem/configUtils';
import { defaultIconFor } from '@src/utils/columnMetricSystem/adapters';
import { PresetDropdown } from '@Components/PresetDropdown/PresetDropdown';
import { RenameModal } from '@Components/RenameModal/RenameModal';
import { Delivery } from './Delivery/Delivery';
import { DeliveryMetricOption } from './Delivery/SingleChannelDeliveryChart';
import { MatchedResponsesModal } from './MatchedResponsesModal';
import {
  downloadMatchedResponses,
  HIDE_MATCHED_RESPONSES_ALERT_KEY
} from './matchedResponsesUtils';
import { EditPagePresetModal } from './MetricsTableConfig/EditPagePresetModal';
import { EditTablePresetModal } from './MetricsTableConfig/EditTablePresetModal';
import { DailyDeliveryData } from './Delivery/delivery-fetch';
import { resolveDeliveryDimension, resolveDeliveryFilter } from './Delivery/delivery-utils';
import { useGroupedSegments } from '../SegmentationMapping/segmentationMappingUtils';
import { MatchableRates } from './MatchableRates';
import { LiftFromTvChart } from './LiftFromTvChart';
import { ConversionLatencyHistogram } from './ConversionLatencyHistogram';
import { TimeTravelModal } from './TimeTravelModal';

// The comet lambda proxy can respond with an error field that none of the typed response
// shapes declare (it's a transport-level escape hatch, not part of the real payload contract).
const getCometErrorMessage = (data: unknown): string | undefined => {
  const message = (data as Record<string, unknown> | null | undefined)?.cometErrorMessage;
  return typeof message === 'string' ? message : undefined;
};

// A calculated metric's value is the server's SQL cell when the response has one, else
// `aggregator(datum)` over the row's base columns (e.g. revenue/spend for ROAS). Charts plot a
// missing value as 0.
const evaluateOverviewMetric = (
  meta: ColumnMetaData | undefined,
  datum: AggregatorDatum,
  metricKey: string
): number => {
  const server =
    meta?.aggregator && isServerValueUsable(meta, metricKey)
      ? readServerValue(datum, metricKey)
      : undefined;
  let value: unknown;
  if (server?.found) {
    value = server.value;
  } else {
    value = meta?.aggregator ? meta.aggregator(datum) : datum[metricKey];
  }
  return typeof value === 'number' ? value : 0;
};

// The metrics table's data for a fetch key. While that key loads after an in-table search change,
// fall back to the last response for the same query under any search (`baseKey`), minus its
// totals, which belong to that search; the client filter narrows its rows meanwhile.
const getTableData = <T extends { totals?: unknown }>(
  lastByBaseKey: Record<string, T | undefined>,
  key: string,
  baseKey: string,
  keyData: T | undefined
): T | undefined => {
  if (keyData) {
    // eslint-disable-next-line no-param-reassign
    lastByBaseKey[baseKey] = keyData;
    return keyData;
  }
  const last = lastByBaseKey[baseKey];
  return last && key !== baseKey ? { ...last, totals: undefined } : undefined;
};

// Spark-chart header totals from the server's totals row, keyed by spark option.
const getSparkServerTotals = (
  totals: ServerValueMap | undefined,
  sparkMetricNames: Record<string, string>,
  customColumnMetaDataMap: Record<string, ColumnMetaData>
): Partial<Record<string, number | null>> | undefined => {
  if (!totals) return undefined;
  const result: Partial<Record<string, number | null>> = {};
  const add = (option: string, name: string) => {
    const server = readServerValue(totals, name);
    if (server.found) {
      result[option] = server.value;
    }
  };
  Object.entries(sparkMetricNames).forEach(([option, name]) => add(option, name));
  Object.entries(customColumnMetaDataMap).forEach(([name, meta]) => {
    if (isServerValueUsable(meta, name)) {
      add(name, name);
    }
  });
  return result;
};

const enum TabKey {
  DELIVERY = 'delivery',
  METRICS_TABLE = 'metrics-table',
  OVERVIEW = 'overview'
}

const NAVS = [
  { label: 'Overview', key: TabKey.OVERVIEW },
  { label: 'Delivery', key: TabKey.DELIVERY },
  { label: 'Metrics Table', key: TabKey.METRICS_TABLE }
];

const PRINCIPAL_BASED_BUYING_NAVS = [{ label: 'Overview', key: TabKey.OVERVIEW }];

const METRICS_TABLE_ROW_LIMIT = 1000;
const METRICS_TABLE_DISPLAY_CAP = 50;

export const DATE_GROUPING_MAP: Record<
  'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly',
  DateGroupingKey
> = {
  daily: 'Day',
  weekly: 'Week',
  monthly: 'Month',
  quarterly: 'Quarter',
  yearly: 'Year'
};

const DEFAULT_SPARK_CHART_METRICS_MAP = {
  clicks: 'clicks',
  cpm: 'cpm',
  cpx: 'cpx',
  impressions: 'impressions',
  revenue: 'revenue',
  roas: 'roas',
  spend: 'spend',
  volume: 'volume'
};

interface SingleChannelPageProps {
  columnMetaDataMap: ColumnMetaDataMap;
  dataFetchKey: (args) => string;
  dateIncrementOptions?: ('daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly')[];
  dates: DateRange;
  defaultKPI?: string;
  defaultLag?: S.LagOption;
  defaultSparkChartMetrics?: string[];
  defaultCompareMetrics?: [string, string];
  defaultDeliveryOverviewMetrics?: [string, string, string];
  deletePagePreset: (params: DeletePagePresetParams) => Promise<void>;
  deleteTablePreset: (params: DeleteTablePresetParams) => Promise<void>;
  deliveryDimensionMap: Record<string, string>;
  deliveryMetricOptions?: DeliveryMetricOption[];
  dimensionColumnMetaDataMap: ColumnMetaDataMap;
  fetchingPagePresets: boolean;
  fetchingTablePresets: boolean;
  filterData: P.GetFilterOptionsResponse;
  filterDataProcessor?: (data: P.GetFilterOptionsResponse, ...args) => P.GetFilterOptionsResponse;
  getDeliveryData: (params) => Promise<DailyDeliveryData>;
  getPerformanceData: (
    params,
    extra
  ) => Promise<
    S.GetPageDataResponse &
      L.GetPageDataResponse &
      Y.GetPageDataResponse &
      Pick<ChannelPageDataResponse, 'totals'>
  >;
  getDimensionCell: (
    dimensionData: DimensionMap,
    dimensionHeader: DimensionColumn,
    ...args: any[]
  ) => CellData;
  getTablePreset: (params: GetTablePresetParams) => Promise<MetricsTablePreset | undefined>;
  glossary?: GlossaryItem[];
  isGraph?: boolean;
  kpiMetaData: P.GetKpiMetaDataResponse;
  pagePresets: MetricsPagePreset[];
  percentAOIPlaceholder?: number;
  prefix: SingleChannelMediaTypes;
  savePagePreset: (
    body: Partial<SavePagePresetParams>
  ) => Promise<{ id: number; name: string } | { message: string }>;
  saveTablePreset: (body: Partial<SaveTablePresetParams>) => Promise<{ id: number; name: string }>;
  setDates: Dispatch<SetStateAction<DateRange>>;
  selectedSources?: string[];
  setSelectedSources?: Dispatch<SetStateAction<string[]>>;
  setPagePresets: Dispatch<SetStateAction<MetricsPagePreset[] | undefined>>;
  setTablePresets: Dispatch<SetStateAction<MetricsTablePreset[] | undefined>>;
  sourceOptions?: SourceOptions;
  sparkChartMetricPrettyNameMap?: Record<string, string>;
  sparkChartMetricsMap: Record<string, string>;
  tablePresets: PresetIDGroups[];
  updateSettings?: (args) => void;
  useCrossChannelKpis?: boolean;
  analyticsIsSource?: boolean;
  platformOptions?: PlatformOptions;
}

export const SingleChannelPage = ({
  columnMetaDataMap: standardColumnMetaDataMap = {},
  dataFetchKey,
  dateIncrementOptions = ['daily', 'weekly', 'monthly', 'quarterly', 'yearly'],
  dates,
  defaultKPI,
  defaultLag,
  defaultSparkChartMetrics = ['spend', 'impressions', 'volume', 'roas'],
  defaultCompareMetrics,
  defaultDeliveryOverviewMetrics,
  deletePagePreset,
  deleteTablePreset,
  deliveryDimensionMap = {},
  deliveryMetricOptions: standardDeliveryMetricOptions,
  dimensionColumnMetaDataMap = {},
  fetchingPagePresets,
  fetchingTablePresets,
  filterData,
  filterDataProcessor = rawFilterData => rawFilterData,
  getDeliveryData,
  getDimensionCell,
  getPerformanceData,
  getTablePreset,
  glossary = [],
  isGraph,
  kpiMetaData,
  pagePresets,
  percentAOIPlaceholder,
  prefix,
  savePagePreset,
  saveTablePreset,
  selectedSources,
  setSelectedSources,
  setDates,
  setPagePresets,
  setTablePresets,
  sourceOptions,
  sparkChartMetricPrettyNameMap = {},
  sparkChartMetricsMap = DEFAULT_SPARK_CHART_METRICS_MAP,
  tablePresets,
  updateSettings = () => {},
  platformOptions,
  useCrossChannelKpis = false,
  analyticsIsSource = false
}: SingleChannelPageProps) => {
  const [branchBuild, setBranchBuild] = useState('v2/latest');
  const shouldEnableProgrammaticPages = useExperimentFlag('enableUseTTDProgramatic');
  const shouldEnableTimeTravel = useExperimentFlag('enableTimeTravel');
  const [timeTravelOpen, setTimeTravelOpen] = useState(false);
  const shouldEnableImpsBetweenToggle = useExperimentFlag('enableImpsBetweenToggle');
  const shouldEnableByResponseDate = useExperimentFlag('enableByResponseDate');

  const [customColumnMetaDataMap, setCustomColumnMetaDataMap] =
    useState<Record<string, ColumnMetaData>>();
  const [dataMap, setDataMap] = useMap<
    string,
    (S.GetPageDataResponse & Pick<ChannelPageDataResponse, 'totals'>) | undefined
  >();
  const [dataStatusMap, setDataStatusMap] = useMap<string, string | undefined>();
  const [deliveryDataMap, setDeliveryDataMap] = useMap<string, any>();
  const [deliveryDataStatusMap, setDeliveryDataStatusMap] = useMap<string, string | undefined>();
  const [selectedDeliveryDimension, setDeliveryDimension] = useState<string>(
    R.keys(deliveryDimensionMap)[0]
  );
  const deliveryDimension = resolveDeliveryDimension(
    selectedDeliveryDimension,
    deliveryDimensionMap
  );

  // The shared MetricsTable exposes its export data imperatively (lazily computed on
  // demand) instead of pushing it up through a callback prop into state.
  const metricsTableRef = useRef<MetricsTableHandle>(null);
  const [filter, setFilter] = useState<{ filter: (line: Record<string, any>) => boolean }>();
  // The in-table search as server `tableFilter` terms, so totals follow it; undefined for an
  // advanced expression the server can't express (totals then stay client-side).
  const [tableFilterTerms, setTableFilterTerms] = useState<string[] | undefined>([]);
  const [filterID, setFilterID] = useState<number | undefined>();
  const [filters, setFilters] = useState<FilterPreset[]>();
  const [filterSource, setFilterSource] = useState<
    'presetFilterID' | 'currentFilterID' | 'presetState' | 'currentState'
  >();
  const [filterState, setFilterState, resetFilterState] = useFilterPaneState();
  const [loading, setLoading] = useState<boolean>(false);
  const [showLargeDatasetModal, setShowLargeDatasetModal] = useState(false);
  const fullDataByKeyRef = useRef<Record<string, any>>({});
  const truncatedKeysRef = useRef<Set<string>>(new Set());
  const [otherDates, setOtherDates] = useState<DateRange>();
  const [pageGroupName, setPageGroupName] = useState<string>('');
  const [pagePresetChanges, setPagePresetChanges] = useState<MetricsPagePreset>({
    globalOptions: {}
  } as MetricsPagePreset);
  const [pagePresetName, setPagePresetName] = useState<string>();
  const [tablePresetName, setTablePresetName] = useState<string>();
  const [pickerAudience, setPickerAudience] = useState<LinearAudience>();
  const [pickerKPI, setPickerKPI] = useState<string>('');
  const [pickerLag, setPickerLag] = useState<S.LagOption>();
  const [defaultLagWindow, setDefaultLagWindow] = useState<S.LagOption>();
  const isLagChannel = isQueryArgOnChannel('lag', prefix);
  const [rerenderTable, setRerenderTable] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedTab, setSelectedTab] = useState<string>();
  const [showEditPagePresetModal, setShowEditPagePresetModal] = useState<boolean>(false);
  const [showEditTablePresetModal, setShowEditTablePresetModal] = useState<boolean>(false);
  const [showGlossary, setShowGlossary] = useState<boolean>(false);
  const [showNewPagePresetModal, setShowNewPagePresetModal] = useState<boolean>(false);
  const [showNewTablePresetModal, setShowNewTablePresetModal] = useState<boolean>(false);
  const [showRenamePagePresetModal, setShowRenamePagePresetModal] = useState<boolean>(false);
  const [showRenameTablePresetModal, setShowRenameTablePresetModal] = useState<boolean>(false);
  const [tableHeight, setTableHeight] = useState(250);
  const [tablePresetChanges, setTablePresetChanges] = useState<MetricsTablePreset>(
    {} as MetricsTablePreset
  );
  const [tablePresetMap, setTablePresetMap] = useMap<string, MetricsTablePreset | undefined>();
  const [tableWidth, setTableWidth] = useState(500);
  const [showMetricsTableComparison, setShowMetricsTableComparison] = useState<boolean>(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { pagePresetName: urlPagePresetName, tablePresetName: urlTablePresetName } = usePresetUrl();
  const company = useLocationCompany() ?? '';
  const companyInfo = useCompanyInfo();
  const fiscalCal = useMemo(() => companyInfo.fiscal_cal || {}, [companyInfo]);
  const containerRef = useRef<HTMLDivElement>(null);
  const isAdmin = useAppSelector(UserRedux.isAdminSelector);
  const isInternal = useAppSelector(UserRedux.isInternalSelector);
  const userFetched = useAppSelector(UserRedux.userFetchedSelector);
  const mediaTypes = useAppSelector(mediaTypesSelector(company));
  const setError = useSetError();
  const setAreYouSure = useSetAreYouSure();
  const confirmSave = useCallback(
    (onOkay: () => void, options?: { temporary?: boolean }) => {
      const temporary = options?.temporary;
      setAreYouSure({
        title: temporary ? 'Save Temporary View' : 'Save Preset',
        message: temporary
          ? "You're saving a temporary view that will only be available during this session unless you later edit and save it as a Table Preset during this session."
          : 'Saving this preset cannot be undone. Are you sure?',
        okayText: 'Save',
        cancelText: 'Cancel',
        intent: 'warning',
        onOkay
      });
    },
    [setAreYouSure]
  );
  const shouldEnablePrincipalBasedBuying = useExperimentFlag('enablePrincipalBasedBuying');
  const shouldEnableMetricsTableSegmentGroups = useExperimentFlag(
    'enableMetricsTableSegmentGroups'
  ) as boolean;
  const shouldShowCostPages =
    !shouldEnablePrincipalBasedBuying || (shouldEnablePrincipalBasedBuying && isInternal);
  // The flag (company/user experiments) and isInternal arrive asynchronously, so the nav list
  // would flip between NAVS and PRINCIPAL_BASED_BUYING_NAVS while loading. Hold it until both are in.
  const navsReady = userFetched && Boolean(companyInfo.cid);
  const pageNavs = shouldShowCostPages ? NAVS : PRINCIPAL_BASED_BUYING_NAVS;

  const [datePickerImpsBetween, setDatePickerImpsBetween] = useState<boolean>();
  const [impsBetween, setImpsBetween] = useState<boolean>();

  const normalizeDate = useCallback(
    (date: string): string => (prefix === LINEAR ? snapToMonday(date) : date),
    [prefix]
  );

  const [showMatchedResponsesModal, setShowMatchedResponsesModal] = useState(false);
  const { groupedSegments } = useGroupedSegments({
    company,
    shouldEnableMetricsTableSegmentGroups
  });
  const usedGroupedSegments = useGroupedSegmentFiltering(filterState, groupedSegments);
  const expAnalyticsCols = useExperimentalAnalyticsCols();

  const deliveryMetricOptions = useMemo(() => {
    if (standardDeliveryMetricOptions && customColumnMetaDataMap) {
      const computedDeliveryMetricOptions = standardDeliveryMetricOptions;
      const availableMetrics = R.map(
        (option: DeliveryMetricOption) => option.value,
        R.defaultTo([], computedDeliveryMetricOptions)
      );
      for (const dataVarName of R.keys(customColumnMetaDataMap)) {
        const colMetaData = customColumnMetaDataMap[dataVarName];
        if (colMetaData && colMetaData.requiredColumns && colMetaData.requiredColumns.length) {
          for (const metric of colMetaData.requiredColumns) {
            if (!availableMetrics.includes(metric)) {
              return computedDeliveryMetricOptions;
            }
          }
          computedDeliveryMetricOptions.push({
            isCustom: true,
            isSpend: ['cpx', 'spend'].includes(
              customColumnMetaDataMap[dataVarName].customMetaData?.formatter ?? ''
            ),
            value: dataVarName,
            label: customColumnMetaDataMap[dataVarName].prettyName
          });
        }
      }
      return computedDeliveryMetricOptions;
    }
    return undefined;
  }, [customColumnMetaDataMap, standardDeliveryMetricOptions]);

  const compareMetricOptions = useMemo(() => {
    const computedCompareMetricOptions: CompareMetricOption[] = R.map(
      key => ({
        label: R.defaultTo(sparkChartMetricsMap[key], sparkChartMetricPrettyNameMap[key]),
        value: key as any
      }),
      R.keys(sparkChartMetricsMap)
    );
    if (computedCompareMetricOptions && customColumnMetaDataMap) {
      for (const dataVarName of R.keys(customColumnMetaDataMap)) {
        const colMetaData = customColumnMetaDataMap[dataVarName];
        if (colMetaData && colMetaData.requiredColumns && colMetaData.requiredColumns.length) {
          computedCompareMetricOptions.push({
            isCustom: true,
            label: customColumnMetaDataMap[dataVarName].prettyName,
            value: dataVarName as Metric
          });
        }
      }
      return computedCompareMetricOptions;
    }
    return undefined;
  }, [customColumnMetaDataMap, sparkChartMetricsMap, sparkChartMetricPrettyNameMap]);

  const mediaTypeEnabled = useMemo(
    () =>
      TEST_COMPANIES.includes(company) ||
      mediaTypes.includes(prefix === LINEAR ? 'tv' : prefix) ||
      (prefix === 'programmatic' && shouldEnableProgrammaticPages),
    [company, mediaTypes, prefix, shouldEnableProgrammaticPages]
  );
  // Mounted at one route per tab (`.../<channel>/<tab>`); the base path is the pathname with the
  // tab segment trimmed.
  const channelBasePath = useMemo(() => {
    // Derived from the pathname (checking for any of the 3 known tab segments) rather than from
    // `selectedTab` state, so it can't go stale relative to `selectedTab`.
    const tabIndices = [TabKey.OVERVIEW, TabKey.METRICS_TABLE, TabKey.DELIVERY]
      .map(tab => location.pathname.indexOf(`/${tab}`))
      .filter(index => index >= 0);
    if (tabIndices.length === 0) return location.pathname;
    return location.pathname.slice(0, Math.min(...tabIndices));
  }, [location.pathname]);

  // One definition per key: the reset handler once removed a lowercase copy of the other key and
  // silently left the stored range in place.
  const dateRangeKey = `${company}_${prefix}PerfDateRange`;
  const otherDateRangeKey = `${company}_${prefix}OtherPerfDateRange`;

  const [startingDateRange, rememberedOtherDateRange] = useMemo(() => {
    const localDateRangeString = localStorage.getItem(dateRangeKey);
    const localDateRange = localDateRangeString ? JSON.parse(localDateRangeString) : null;
    const localOtherDateRangeString = localStorage.getItem(otherDateRangeKey);
    const localOtherDateRange = localOtherDateRangeString
      ? JSON.parse(localOtherDateRangeString)
      : null;
    return [
      {
        start: normalizeDate(
          localDateRange && localDateRange.start
            ? localDateRange.start
            : computeResolvedDate(DEFAULT_DATE_RANGE_STATE.start)
        ),
        end: normalizeDate(
          localDateRange && localDateRange.end
            ? localDateRange.end
            : computeResolvedDate(DEFAULT_DATE_RANGE_STATE.end)
        )
      },
      {
        start: normalizeDate(
          localOtherDateRange && localOtherDateRange.start
            ? localOtherDateRange.start
            : computeResolvedDate(DEFAULT_OTHER_DATE_RANGE_STATE.start)
        ),
        end: normalizeDate(
          localOtherDateRange && localOtherDateRange.end
            ? localOtherDateRange.end
            : computeResolvedDate(DEFAULT_OTHER_DATE_RANGE_STATE.end)
        )
      }
    ];
  }, [dateRangeKey, otherDateRangeKey, normalizeDate]);
  // A remembered/default comparison range that doesn't end before the primary starts is ignored in
  // favor of the default comparison derived from the primary dates.
  const startingOtherDateRange = useMemo(() => {
    const { start, end } = comparisonRangeBefore(
      startingDateRange,
      rememberedOtherDateRange,
      fiscalCal
    );
    return { start: normalizeDate(start), end: normalizeDate(end) };
  }, [startingDateRange, rememberedOtherDateRange, fiscalCal, normalizeDate]);

  const resetPickers = useCallback(() => {
    setDates(undefined as any);
    setFilterSource(undefined);
    setFilterID(undefined);
    setOtherDates(undefined);
    setPickerAudience('' as any);
    setPickerKPI('');
    setPickerLag('' as any);
    setImpsBetween(undefined);
    setDatePickerImpsBetween(undefined);
    if (setSelectedSources) {
      setSelectedSources([]);
    }
  }, [setDates, setSelectedSources]);

  const setURL = useCallback(
    (pagePresetNameInput: string, tablePresetNameInput?: string, selectedTabInput?: string) => {
      // To avoid infinite renders, only update the URL and page preset if it's different from
      // the current state. A `selectedTabInput` that differs from the currently-mounted tab
      // navigates to one of the OTHER top-level `/<channel>/<tab>` routes, which is a full
      // remount of this component (see the routing note on `channelBasePath` above) — that's
      // expected, not a bug.
      const newPath = `${channelBasePath}/${R.defaultTo(selectedTab, selectedTabInput)}`;
      const newSearch = setPresetSearchParams(location.search, {
        pagePresetName: pagePresetNameInput,
        tablePresetName: tablePresetNameInput
      });
      const isLocationDifferent = location.pathname !== newPath || location.search !== newSearch;
      if (
        (selectedTab && isLocationDifferent) ||
        R.isNil(pagePresetName) ||
        R.isNil(tablePresetName)
      ) {
        setPagePresetName(pagePresetNameInput);
        setTablePresetName(tablePresetNameInput);
        navigate(`${newPath}${newSearch}`);
        Mixpanel.track(MxE.PagePresetChange, {
          widget: 'pagePreset',
          option_selected: pagePresetNameInput
        });
      }
    },
    [location, navigate, channelBasePath, selectedTab, pagePresetName, tablePresetName]
  );

  const [tablePreset, tablePresetID] = useMemo(() => {
    if (tablePresetName && tablePresets && tablePresetMap && customColumnMetaDataMap) {
      const matchedTablePreset = R.find(preset => preset.name === tablePresetName, tablePresets);
      if (matchedTablePreset) {
        return [tablePresetMap[`${matchedTablePreset?.id}`], matchedTablePreset.id];
      }
    }
    return [undefined, undefined];
  }, [customColumnMetaDataMap, tablePresetMap, tablePresetName, tablePresets]);

  const pagePreset = useMemo(() => {
    if (pagePresets && tablePresets && !fetchingPagePresets && !fetchingTablePresets) {
      const foundPagePreset = R.find(
        (preset: MetricsPagePreset) => preset.name === pagePresetName,
        pagePresets
      );
      if (foundPagePreset) {
        const workingPagePreset = R.clone(foundPagePreset);
        const presetChangesDraft = R.clone(foundPagePreset);
        // Set Filter Info
        if (workingPagePreset.filterInfo && R.isNil(filterSource)) {
          if (!R.isNil(workingPagePreset.filterInfo.filterID)) {
            setFilterSource('presetFilterID');
            setFilterID(workingPagePreset.filterInfo.filterID);
          } else if (workingPagePreset.filterInfo.filterState) {
            setFilterSource('presetState');
            setFilterState(workingPagePreset.filterInfo.filterState);
          } else {
            setFilterSource('currentState');
            resetFilterState();
          }
        } else {
          presetChangesDraft.filterInfo = {};
          if (filterID) {
            presetChangesDraft.filterInfo.filterID = filterID;
          } else if (filterState) {
            presetChangesDraft.filterInfo.filterState = filterState;
          }
        }
        // Set Global Options
        if (workingPagePreset.globalOptions) {
          if (!pickerAudience) {
            const audience = workingPagePreset?.globalOptions.audience || 'HH';
            setPickerAudience(audience);
          } else {
            presetChangesDraft.globalOptions.audience = pickerAudience;
          }
          if (!pickerKPI) {
            const kpi =
              workingPagePreset.globalOptions.kpi ||
              defaultKPI ||
              (useCrossChannelKpis
                ? companyInfo.cross_channel_kpis[0]
                : companyInfo.streaming_performance_default_kpi);
            setPickerKPI(kpi);
            presetChangesDraft.globalOptions.kpi = kpi;
          } else {
            presetChangesDraft.globalOptions.kpi = pickerKPI;
          }
          if (!pickerLag) {
            const lag =
              workingPagePreset.globalOptions.lag ||
              defaultLag ||
              // Lag channels: no saved lag means `default`, so do not write the company lag.
              (isLagChannel ? undefined : companyInfo.streaming_performance_default_lag);
            setPickerLag(lag);
            presetChangesDraft.globalOptions.lag = lag;
          } else {
            presetChangesDraft.globalOptions.lag = pickerLag;
          }
          if (!R.isNil(impsBetween)) {
            presetChangesDraft.globalOptions.impsBetween = impsBetween;
            setDatePickerImpsBetween(impsBetween);
          } else {
            const presetImpsBetween = R.defaultTo(
              true,
              workingPagePreset.globalOptions.impsBetween
            );
            setImpsBetween(presetImpsBetween);
            setDatePickerImpsBetween(presetImpsBetween);
            presetChangesDraft.globalOptions.impsBetween = presetImpsBetween;
          }

          const { source } = workingPagePreset.globalOptions;

          if (source && sourceOptions && setSelectedSources) {
            setSelectedSources(s => (s.length === 0 ? [source] : s));
          } else if (sourceOptions && setSelectedSources) {
            // If not defined reset to platform
            setSelectedSources(s => (s.length === 0 ? ['platform'] : s));
          }
        } else {
          presetChangesDraft.globalOptions = {} as GlobalOptions;
          if (pickerKPI) {
            presetChangesDraft.globalOptions.kpi = pickerKPI;
          }
          if (pickerAudience) {
            presetChangesDraft.globalOptions.audience = pickerAudience;
          }
          if (pickerLag) {
            presetChangesDraft.globalOptions.lag = pickerLag;
          }
          if (!R.isNil(impsBetween)) {
            presetChangesDraft.globalOptions.impsBetween = impsBetween;
          }
        }
        // Set Dates
        if (workingPagePreset.dateInfo) {
          const resolved = resolvePresetDateInfo(
            workingPagePreset.dateInfo,
            fiscalCal,
            startingOtherDateRange
          );
          let resolvedPrimaryDates = resolved.primary;
          if (resolvedPrimaryDates) {
            resolvedPrimaryDates = {
              start: normalizeDate(resolvedPrimaryDates.start),
              end: normalizeDate(resolvedPrimaryDates.end)
            };
            workingPagePreset.dateInfo = {
              ...R.defaultTo({}, workingPagePreset.dateInfo),
              ...resolvedPrimaryDates
            } as DateInfo;
          } else {
            setDates(startingDateRange);
            workingPagePreset.dateInfo = {
              ...R.defaultTo({}, workingPagePreset.dateInfo),
              ...startingDateRange
            } as DateInfo;
          }
          if (R.isNil(dates)) {
            setDates(workingPagePreset.dateInfo);
            presetChangesDraft.dateInfo = workingPagePreset.dateInfo;
          } else {
            presetChangesDraft.dateInfo = {
              ...R.defaultTo({}, presetChangesDraft.dateInfo),
              ...dates
            } as DateInfo;
          }
          /// Set Comparison Dates
          let resolvedComparisonDates = resolved.comparison;
          resolvedComparisonDates = {
            start: normalizeDate(resolvedComparisonDates.start),
            end: normalizeDate(resolvedComparisonDates.end)
          };
          workingPagePreset.dateInfo = {
            ...workingPagePreset.dateInfo,
            comparison_start: resolvedComparisonDates.start,
            comparison_end: resolvedComparisonDates.end
          };
          if (R.isNil(otherDates)) {
            setOtherDates({
              start: workingPagePreset.dateInfo.comparison_start as string,
              end: workingPagePreset.dateInfo.comparison_end as string
            });
            presetChangesDraft.dateInfo = workingPagePreset.dateInfo;
          } else {
            presetChangesDraft.dateInfo = {
              ...R.defaultTo({}, presetChangesDraft.dateInfo),
              comparison_start: otherDates.start,
              comparison_end: otherDates.end
            } as DateInfo;
          }
        } else {
          if (R.isNil(dates)) {
            setDates(startingDateRange);
            presetChangesDraft.dateInfo = {
              ...R.defaultTo({}, presetChangesDraft.dateInfo),
              ...startingDateRange
            } as DateInfo;
          }
          if (R.isNil(otherDates)) {
            setOtherDates(startingOtherDateRange);
            presetChangesDraft.dateInfo = {
              ...R.defaultTo({}, presetChangesDraft.dateInfo),
              comparison_start: startingOtherDateRange.start,
              comparison_end: startingOtherDateRange.end
            } as DateInfo;
          }
        }
        // Set Table Preset
        if (
          R.isNil(
            R.find(candidatePreset => candidatePreset.name === tablePresetName, tablePresets)
          ) &&
          R.isNil(tablePresetID) &&
          tablePresetMap &&
          workingPagePreset.globalOptions
        ) {
          const pageSourcedTableID = workingPagePreset.globalOptions.tableID;
          const pageSourcedTablePreset = R.find(
            preset => preset.id === pageSourcedTableID,
            tablePresets
          );
          if (pageSourcedTablePreset) {
            setURL(workingPagePreset.name, pageSourcedTablePreset.name);
          } else if (tablePresets && tablePresets.length) {
            for (const firstNonTemporaryPreset of tablePresets) {
              if (!firstNonTemporaryPreset.temporary) {
                workingPagePreset.globalOptions.tableID = firstNonTemporaryPreset.id;
                presetChangesDraft.globalOptions.tableID = firstNonTemporaryPreset.id;
                setURL(workingPagePreset.name, firstNonTemporaryPreset.name);
                break;
              }
            }
          }
        } else if (!R.isNil(tablePresetID)) {
          presetChangesDraft.globalOptions.tableID = tablePresetID;
        }
        // Set Delivery Dimension
        if (workingPagePreset.globalOptions && workingPagePreset.globalOptions.deliveryDimension) {
          setDeliveryDimension(workingPagePreset.globalOptions.deliveryDimension);
        }
        setPagePresetChanges(presetChangesDraft);
        return workingPagePreset;
      }
    }
    return undefined;
  }, [
    isLagChannel,
    companyInfo,
    dates,
    defaultKPI,
    defaultLag,
    fetchingPagePresets,
    fetchingTablePresets,
    filterID,
    filterSource,
    filterState,
    fiscalCal,
    impsBetween,
    otherDates,
    pagePresetName,
    pagePresets,
    pickerAudience,
    pickerKPI,
    pickerLag,
    resetFilterState,
    setDates,
    setFilterState,
    setSelectedSources,
    setURL,
    normalizeDate,
    sourceOptions,
    startingDateRange,
    startingOtherDateRange,
    tablePresetID,
    tablePresetMap,
    tablePresetName,
    tablePresets,
    useCrossChannelKpis
  ]);

  const pagePresetGroups = useMemo(() => {
    const groupsMap = {};
    if (pagePresets) {
      for (const preset of pagePresets) {
        if (preset.presetGroup && preset.presetGroup !== STANDARD_GROUP_NAME) {
          groupsMap[preset.presetGroup] = true;
        }
      }
    }
    return R.keys(groupsMap).sort();
  }, [pagePresets]);

  const activeFilterStateOrPreset = useMemo(() => {
    // we want the flexibility to grab the whole preset since it include tokens
    // for advanced parsing but if not the filter state is next best option
    let currActiveFilterState;

    if (filterSource === 'currentState' && !R.isNil(filterState)) {
      currActiveFilterState = filterState;
    } else if (filterSource === 'currentFilterID' && !R.isNil(filterID) && filters) {
      // find the filter with the right id
      const filterFromId = filters.find(f => f.id === filterID);
      if (filterFromId) {
        currActiveFilterState = filterFromId;
      }
    } else if (
      pagePreset &&
      filterSource === 'presetFilterID' &&
      !R.isNil(pagePreset.filterInfo.filterID) &&
      filters
    ) {
      const presetFilterFromId = filters.find(f => f.id === pagePreset.filterInfo.filterID);
      if (presetFilterFromId) {
        currActiveFilterState = presetFilterFromId;
      }
    } else if (pagePreset && filterSource === 'presetState' && pagePreset.filterInfo.filterState) {
      currActiveFilterState = pagePreset.filterInfo.filterState;
    }
    return currActiveFilterState;
  }, [filterSource, filters, filterID, filterState, pagePreset]);

  // These global options can come from the pickers, page preset, or default company settings
  // Order of importance would be pickers > page preset > company settings
  const [fetchAudience, fetchKPI, fetchLag, globalBrand] = useMemo(() => {
    const defAudience: LinearAudience = DEFAULT_LINEAR_AUDIENCE;
    const defKpi = R.defaultTo(companyInfo.streaming_performance_default_kpi, defaultKPI);
    const defLag: LagId = isLagChannel
      ? DEFAULT_LAG_ID
      : R.defaultTo(companyInfo.streaming_performance_default_lag, defaultLag);
    const {
      audience: presetAudience,
      kpi: presetKPI,
      lag: presetLag
    } = pagePreset && pagePreset.globalOptions
      ? pagePreset.globalOptions
      : {
          audience: undefined,
          kpi: undefined,
          lag: undefined
        };
    return [
      pickerAudience || presetAudience || defAudience,
      pickerKPI || presetKPI || defKpi,
      pickerLag || presetLag || defLag,
      getGlobalBrand(
        companyInfo,
        R.defaultTo(defAudience, R.defaultTo(presetAudience, pickerAudience))
      )
    ];
  }, [
    isLagChannel,
    companyInfo,
    defaultKPI,
    defaultLag,
    pagePreset,
    pickerAudience,
    pickerKPI,
    pickerLag
  ]);

  const shownLag = fetchLag === DEFAULT_LAG_ID ? defaultLagWindow : fetchLag;

  const columnMetaDataMap = useMemo(
    () => ({ ...standardColumnMetaDataMap, ...R.defaultTo({}, customColumnMetaDataMap) }),
    [customColumnMetaDataMap, standardColumnMetaDataMap]
  );

  // Custom metrics are not in METRIC_TO_FORMATTER, so charts use their own formatter,
  // defaulted to the metric's configured decimals.
  const metricFormatter = useMemo((): MetricFormatter => {
    const baseFormatter = getMetricFormatter(companyInfo.currency_code);
    return metric => {
      const { formatter, decimals } = columnMetaDataMap[metric] ?? {};
      if (metric in METRIC_TO_FORMATTER || !formatter) {
        return baseFormatter(metric);
      }
      return (value, options) => formatter(value, { decimals, ...options });
    };
  }, [companyInfo.currency_code, columnMetaDataMap]);

  // Fetches the company's default lag window for the "7d (Default)" label.
  // `cancelled` drops a late response after the company or channel changes.
  useEffect(() => {
    let cancelled = false;
    setDefaultLagWindow(undefined);
    if (isLagChannel) {
      (async () => {
        try {
          const res = await SingleChannelLambdaFetch<{ company: string; channel: string }>(
            '/getDefaultLag',
            { params: { company, channel: prefix } }
          );
          const { lag } = await awaitJSON<{ lag?: S.LagOption }>(res);
          if (!cancelled) {
            setDefaultLagWindow(lag);
          }
        } catch {
          // Label-only lookup: data fetches still send `default`, so do not block the page.
          console.error(`Failed to fetch default lag for ${company}, ${prefix}`);
        }
      })();
    }
    return () => {
      cancelled = true;
    };
  }, [company, isLagChannel, prefix]);

  const serverTableFilter =
    selectedTab === TabKey.METRICS_TABLE && tableFilterTerms?.length
      ? JSON.stringify(tableFilterTerms)
      : undefined;

  // Performance and delivery data are cached separately: each key holds only what its own request
  // sends, so (e.g.) a delivery-dimension change doesn't refetch the metrics table data.
  const getFetchKeys = useCallback(
    (range: DateRange | undefined) => {
      if (!(
        pagePreset &&
        range &&
        branchBuild &&
        (fetchKPI || useCrossChannelKpis) &&
        fetchLag &&
        fetchAudience &&
        !R.isNil(tablePresetID)
      )) {
        return { performanceKey: '', performanceBaseKey: '', deliveryKey: '' };
      }
      const customFilterState =
        filterSource === 'currentState' ? `_${JSON.stringify(filterState)}` : '';
      const customFilterSettings =
        filterState && pagePreset.filterInfo.filterState
          ? R.equals(filterState, pagePreset.filterInfo.filterState)
          : false;
      const sources = [...(selectedSources || [''])].sort().join('-');
      const dateKey = `${pagePreset.id}_${range.start}_${range.end}`;
      const filterKey = `${filterID}_${customFilterSettings}`;
      const performanceBaseKey = `${dateKey}_${branchBuild}_${fetchKPI}_${fetchLag}_${fetchAudience}_${filterKey}_${tablePresetID}_${selectedTab}${customFilterState}_${sources}_${impsBetween}`;
      return {
        performanceKey: `${performanceBaseKey}${serverTableFilter ? `_${serverTableFilter}` : ''}`,
        performanceBaseKey,
        deliveryKey: `${dateKey}_${fetchKPI}_${fetchAudience}_${filterKey}_${deliveryDimension}${customFilterState}_${sources}_${impsBetween}`
      };
    },
    [
      branchBuild,
      deliveryDimension,
      fetchAudience,
      fetchKPI,
      fetchLag,
      filterID,
      filterSource,
      filterState,
      impsBetween,
      pagePreset,
      selectedSources,
      selectedTab,
      serverTableFilter,
      tablePresetID,
      useCrossChannelKpis
    ]
  );
  const {
    performanceKey: fetchKey,
    performanceBaseKey,
    deliveryKey: deliveryFetchKey
  } = useMemo(() => getFetchKeys(dates), [dates, getFetchKeys]);
  const truncatedNoteRef = useRef<HTMLParagraphElement>(null);
  const metricsTableTruncated =
    selectedTab === TabKey.METRICS_TABLE && !loading && truncatedKeysRef.current.has(fetchKey);
  useEffect(() => {
    if (metricsTableTruncated) {
      const timer = setTimeout(() => {
        truncatedNoteRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 200);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [metricsTableTruncated]);
  const {
    performanceKey: otherFetchKey,
    performanceBaseKey: otherPerformanceBaseKey,
    deliveryKey: otherDeliveryFetchKey
  } = useMemo(() => getFetchKeys(otherDates), [otherDates, getFetchKeys]);

  const data = dataMap[fetchKey];
  const otherData = dataMap[otherFetchKey];
  const lastDataByBaseKeyRef = useRef<Record<string, typeof data>>({});
  const metricsTableData = useMemo(
    () => getTableData(lastDataByBaseKeyRef.current, fetchKey, performanceBaseKey, data),
    [data, fetchKey, performanceBaseKey]
  );
  const otherMetricsTableData = useMemo(
    () =>
      getTableData(lastDataByBaseKeyRef.current, otherFetchKey, otherPerformanceBaseKey, otherData),
    [otherData, otherFetchKey, otherPerformanceBaseKey]
  );
  const deliveryData = deliveryDataMap[deliveryFetchKey];
  const otherDeliveryData = deliveryDataMap[otherDeliveryFetchKey];

  useEffect(() => {
    // Whenever we change company with the nav bar, return the page to the state on page load
    if (company && pagePresets && !R.map(R.prop('company'), pagePresets).includes(company)) {
      setPagePresets(undefined);
      setTablePresets(undefined);
      setPagePresetName(undefined);
      resetPickers();
    }
  }, [company, pagePresets, resetPickers, setPagePresets, setPagePresetName, setTablePresets]);

  useEffect(() => {
    updateSettings({
      pagePreset,
      pickerAudience,
      pickerKPI,
      pickerLag,
      impsBetween,
      datePickerImpsBetween,
      setPickerAudience,
      setPickerKPI,
      setPickerLag,
      setTablePresetName,
      setDatePickerImpsBetween,
      setImpsBetween,
      tablePreset
    });
  }, [
    pagePreset,
    pickerAudience,
    pickerKPI,
    pickerLag,
    tablePreset,
    updateSettings,
    impsBetween,
    datePickerImpsBetween
  ]);

  // Derived from `location.pathname` (which tab-route we're mounted at).
  useEffect(() => {
    if (R.isNil(selectedTab)) {
      if (shouldShowCostPages && location.pathname.includes(`/${TabKey.DELIVERY}`)) {
        setSelectedTab(TabKey.DELIVERY);
      } else if (shouldShowCostPages && location.pathname.includes(`/${TabKey.METRICS_TABLE}`)) {
        setSelectedTab(TabKey.METRICS_TABLE);
      } else if (location.pathname.includes(`/${TabKey.OVERVIEW}`)) {
        setSelectedTab(TabKey.OVERVIEW);
      } else {
        setSelectedTab(TabKey.OVERVIEW);
      }
    }
  }, [selectedTab, location.pathname, shouldShowCostPages]);

  useEffect(() => {
    if (
      selectedTab &&
      pagePresets &&
      pagePresets.length &&
      tablePresets &&
      tablePresets.length &&
      !fetchingPagePresets &&
      !fetchingTablePresets &&
      !pagePresetName // We want to only use this logic when we load up the page
    ) {
      const urlPagePreset = R.find(preset => preset.name === urlPagePresetName, pagePresets);
      const urlTablePreset = R.find(preset => preset.name === urlTablePresetName, tablePresets);
      // Page Preset Load Up Cases
      // 1. Page Preset from URL
      // 2. Default Page Preset defined
      // 3. Load first page preset that isn't temporary
      let defaultPagePreset = urlPagePreset;
      if (R.isNil(defaultPagePreset)) {
        for (const preset of pagePresets) {
          if (
            (preset && preset.globalOptions && preset.globalOptions.isDefault) ||
            (R.isNil(defaultPagePreset) && !preset.temporary)
          ) {
            defaultPagePreset = preset;
          }
        }
      }
      // Table Preset Load Up Cases
      // 1. Table Preset from URL
      // 2. Default Table Preset from the page preset selected
      // 3. Load first table preset that isn't temporary
      let defaultTablePreset = urlTablePreset;
      if (R.isNil(defaultTablePreset)) {
        for (const preset of tablePresets) {
          if (
            (preset && preset.id === defaultPagePreset?.globalOptions?.tableID) ||
            (R.isNil(defaultTablePreset) && !preset.temporary)
          ) {
            defaultTablePreset = preset;
          }
        }
      }
      if (defaultPagePreset && defaultTablePreset) {
        setURL(defaultPagePreset.name, defaultTablePreset.name);
        setPageGroupName(R.defaultTo(ALL_GROUP_NAME, defaultPagePreset.presetGroup));
      }
    }
  }, [
    fetchingPagePresets,
    fetchingTablePresets,
    pageGroupName,
    pagePreset,
    pagePresetName,
    pagePresets,
    selectedTab,
    setURL,
    tablePreset,
    tablePresetName,
    tablePresets,
    urlPagePresetName,
    urlTablePresetName,
    prefix
  ]);

  useEffect(() => {
    if (
      !R.isNil(tablePresetID) &&
      !tablePreset &&
      pagePreset &&
      tablePresetMap &&
      R.map(preset => preset.id, R.defaultTo([], tablePresets) as PresetIDGroups[]).includes(
        tablePresetID
      )
    ) {
      (async () => {
        try {
          const fetchedTablePreset = await getTablePreset({
            id: tablePresetID,
            company,
            mediatype: prefix
          });
          if (fetchedTablePreset) {
            setTablePresetMap(`${tablePresetID}`, fetchedTablePreset);
            setTablePresetChanges(fetchedTablePreset);
            setURL(pagePreset.name, fetchedTablePreset.name);
          }
        } catch (e) {
          setError({
            reportError: e,
            message: e.message
          });
        }
      })();
    } else if (pagePreset && tablePresetMap[`${tablePresetID}`]) {
      setURL(pagePreset.name, (tablePresetMap[`${tablePresetID}`] as MetricsTablePreset).name);
    }
  }, [
    setError,
    company,
    prefix,
    companyInfo,
    startingDateRange,
    tablePreset,
    getTablePreset,
    setTablePresetMap,
    pagePreset,
    tablePresets,
    tablePresetMap,
    setURL,
    tablePresetName,
    tablePresetID
  ]);

  const onDatePickerChange = useCallback(
    (newDates: DateRange) => {
      localStorage.setItem(dateRangeKey, JSON.stringify(newDates));
      setDates(newDates);
    },
    [dateRangeKey, setDates]
  );

  const onOtherDatePickerChange = useCallback(
    (newOtherDates: DateRange) => {
      localStorage.setItem(otherDateRangeKey, JSON.stringify(newOtherDates));
      setOtherDates(newOtherDates);
    },
    [otherDateRangeKey, setOtherDates]
  );

  const resetDefaultDates = useCallback(() => {
    localStorage.removeItem(dateRangeKey);
    localStorage.removeItem(otherDateRangeKey);
    setDates({
      start: normalizeDate(computeResolvedDate(DEFAULT_DATE_RANGE_STATE.start)),
      end: normalizeDate(computeResolvedDate(DEFAULT_DATE_RANGE_STATE.end))
    });
    setOtherDates({
      start: normalizeDate(computeResolvedDate(DEFAULT_DATE_RANGE_STATE.start)),
      end: normalizeDate(computeResolvedDate(DEFAULT_DATE_RANGE_STATE.end))
    });
  }, [dateRangeKey, otherDateRangeKey, setDates, setOtherDates, normalizeDate]);

  // We don't want to check for the filter state if we don't have a preset and
  // if we haven't loaded the filter state from the page/filter preset
  const deliveryFilter = useMemo(
    () =>
      resolveDeliveryFilter({
        filterSource,
        filterState,
        filterID,
        presetFilterInfo: pagePreset?.filterInfo
      }),
    [filterSource, filterState, filterID, pagePreset]
  );

  // Delivery Data
  const fetchDeliveryData = useCallback(
    (key, deliveryDates) => {
      (async () => {
        try {
          setLoading(true);
          const params: any = {
            agency: companyInfo.agency,
            company,
            dates: deliveryDates,
            dimension: deliveryDimensionMap[deliveryDimension],
            filterState: {
              advanced: '',
              basic: { notMap: {}, selectedMap: {} },
              isAdvanced: false
            },
            prefix,
            // The following params are used for the new Single Channel delivery lambda. Will probably
            // refactor this once we have all channels using it.
            channel: prefix,
            start: deliveryDates?.start,
            end: deliveryDates?.end,
            pageID: 0 // use this param so that lambdas knows to use the right test company for single channel
          };
          if (fetchKPI) {
            params.kpi = fetchKPI;
          }
          if (fetchAudience) {
            params.audience = fetchAudience;
          }
          Object.assign(params, deliveryFilter);
          const deliveryResult = await getDeliveryData(params);
          const cometErrorMessage = getCometErrorMessage(deliveryResult);
          if (cometErrorMessage) {
            setDeliveryDataMap(key, {
              data: [],
              lastModified: ''
            });
            throw new Error(cometErrorMessage);
          }
          setDeliveryDataMap(key, deliveryResult);
        } catch (e) {
          setDeliveryDataMap(key, {
            data: [],
            lastModified: ''
          });
          setDeliveryDataStatusMap(key, 'readyToFetch');
          setError({
            reportError: e,
            message: e.message
          });
        } finally {
          setLoading(false);
        }
      })();
    },
    [
      company,
      companyInfo.agency,
      deliveryDimension,
      deliveryDimensionMap,
      fetchAudience,
      fetchKPI,
      deliveryFilter,
      getDeliveryData,
      prefix,
      setDeliveryDataMap,
      setDeliveryDataStatusMap,
      setError
    ]
  );

  useEffect(() => {
    if (
      selectedTab === TabKey.DELIVERY &&
      deliveryFetchKey &&
      deliveryDataStatusMap[deliveryFetchKey] === 'readyToFetch'
    ) {
      if (R.isNil(deliveryDataMap[deliveryFetchKey])) {
        try {
          fetchDeliveryData(deliveryFetchKey, dates);
        } catch {
          console.error(
            `Failed to fetch delivery data for ${company}, ${deliveryFetchKey}, ${dates}. Refresh to retry`
          );
          setDeliveryDataStatusMap(deliveryFetchKey, 'failed');
        }
      }
      setDeliveryDataStatusMap(deliveryFetchKey, 'fetched');
    }
  }, [
    company,
    dataMap,
    dataStatusMap,
    dates,
    deliveryDataMap,
    deliveryDataStatusMap,
    fetchDeliveryData,
    deliveryFetchKey,
    selectedTab,
    setDataStatusMap,
    setDeliveryDataStatusMap
  ]);

  useEffect(() => {
    if (
      selectedTab === TabKey.DELIVERY &&
      otherDeliveryFetchKey &&
      deliveryDataStatusMap[otherDeliveryFetchKey] === 'readyToFetch'
    ) {
      if (R.isNil(deliveryDataMap[otherDeliveryFetchKey])) {
        try {
          fetchDeliveryData(otherDeliveryFetchKey, otherDates);
        } catch {
          console.error(
            `Failed to fetch delivery data for ${company}, ${otherDeliveryFetchKey}, ${otherDates}. Refresh to retry`
          );
          setDeliveryDataStatusMap(otherDeliveryFetchKey, 'failed');
        }
      }
      setDeliveryDataStatusMap(otherDeliveryFetchKey, 'fetched');
    }
  }, [
    company,
    dataMap,
    dataStatusMap,
    deliveryDataMap,
    deliveryDataStatusMap,
    fetchDeliveryData,
    otherDates,
    otherDeliveryFetchKey,
    selectedTab,
    setDataStatusMap,
    setDeliveryDataStatusMap
  ]);

  useEffect(() => {
    if (deliveryFetchKey && R.isNil(deliveryDataStatusMap[deliveryFetchKey]) && !deliveryData) {
      setDeliveryDataStatusMap(deliveryFetchKey, 'readyToFetch');
    }
  }, [
    branchBuild,
    company,
    data,
    dataStatusMap,
    dates,
    deliveryData,
    deliveryDataStatusMap,
    fetchDeliveryData,
    deliveryFetchKey,
    fetchKPI,
    fetchLag,
    setDataMap,
    setDataStatusMap,
    setDeliveryDataStatusMap,
    setError
  ]);

  useEffect(() => {
    if (
      otherDeliveryFetchKey &&
      R.isNil(deliveryDataStatusMap[otherDeliveryFetchKey]) &&
      !otherDeliveryData
    ) {
      setDeliveryDataStatusMap(otherDeliveryFetchKey, 'readyToFetch');
    }
  }, [
    branchBuild,
    company,
    data,
    dataStatusMap,
    dates,
    deliveryDataStatusMap,
    fetchDeliveryData,
    deliveryFetchKey,
    fetchKPI,
    fetchLag,
    otherData,
    otherDeliveryData,
    otherDeliveryFetchKey,
    setDataMap,
    setDataStatusMap,
    setDeliveryDataStatusMap,
    setError
  ]);

  // Request params shared by the page's performance fetches (everything but tab-specific options).
  const buildPerformanceParams = useCallback(
    performanceDates => {
      const [branch, build] = branchBuild.split('/');
      const params: any = {
        ...(performanceDates || {}),
        pageID: R.defaultTo({ id: 0 }, pagePreset).id,
        company,
        branch,
        build,
        filterID,
        id: 0, // TODO: remove this
        tableID: tablePresetID,
        mediatype: prefix,
        channel: prefix,
        impsBetween,
        includeMetrics: 'true',
        includeTotals: 'true'
      };
      if (serverTableFilter) {
        params.tableFilter = serverTableFilter;
      }
      if (fetchKPI) {
        params.kpi = fetchKPI;
      }
      if (fetchLag) {
        params.lag = fetchLag;
      }
      if (fetchAudience) {
        params.audience = fetchAudience;
      }
      if (!R.isNil(pagePreset) && !R.isNil(pagePreset.globalOptions)) {
        if (!R.isNil(tablePresetID) && tablePresetID !== pagePreset.globalOptions.tableID) {
          params.tableID = tablePresetID;
        }
        if (pagePreset.globalOptions.shouldUseUnequivalizedImpressions) {
          params.shouldUseUnequivalizedImpressions =
            pagePreset.globalOptions.shouldUseUnequivalizedImpressions;
        }
      }
      if (usedGroupedSegments) {
        params.groupedCategories = JSON.stringify(usedGroupedSegments);
      }
      // We don't want to check for the filter state if we don't have a preset and
      // if we haven't loaded the filter state from the page/filter preset
      if (filterSource === 'currentState' && !R.isNil(filterState)) {
        params.filterState = JSON.stringify(filterState);
      } else if (filterSource === 'currentFilterID' && !R.isNil(filterID)) {
        params.filterID = filterID;
      }
      return params;
    },
    [
      branchBuild,
      company,
      fetchAudience,
      fetchKPI,
      fetchLag,
      filterID,
      filterSource,
      filterState,
      impsBetween,
      pagePreset,
      prefix,
      serverTableFilter,
      tablePresetID,
      usedGroupedSegments
    ]
  );

  // Performance Data
  const fetchPerformanceData = useCallback(
    (key, performanceDates) => {
      (async () => {
        try {
          setLoading(true);
          const params = buildPerformanceParams(performanceDates);
          if (selectedTab === TabKey.OVERVIEW) {
            params.byDate = 'dateOnly';
          }
          const performanceResult = await getPerformanceData(params, { tablePreset });
          const cometErrorMessage = getCometErrorMessage(performanceResult);
          if (cometErrorMessage) {
            setDataMap(key, {
              data: [],
              lastModified: ''
            });
            throw new Error(cometErrorMessage);
          }
          if (
            selectedTab === TabKey.METRICS_TABLE &&
            (performanceResult?.data?.length ?? 0) > METRICS_TABLE_ROW_LIMIT
          ) {
            fullDataByKeyRef.current[key] = performanceResult;
            truncatedKeysRef.current.add(key);
            setShowLargeDatasetModal(true);
            setDataMap(key, {
              ...performanceResult,
              data: performanceResult.data.slice(0, METRICS_TABLE_DISPLAY_CAP)
            });
          } else {
            setDataMap(key, performanceResult);
          }
          if (selectedTab === TabKey.METRICS_TABLE) {
            setLoading(false);
          }
        } catch (e) {
          setDataMap(key, {
            data: [],
            lastModified: ''
          });
          setDataStatusMap(key, 'readyToFetch');
          setError({
            reportError: e,
            message: e.message
          });
          resetPickers();
          setLoading(false);
        } finally {
          setRerenderTable(true);
        }
      })();
    },
    [
      buildPerformanceParams,
      getPerformanceData,
      selectedTab,
      setDataMap,
      setDataStatusMap,
      tablePreset,
      setError,
      resetPickers
    ]
  );

  useEffect(() => {
    if (
      (selectedTab === TabKey.OVERVIEW ||
        (selectedTab === TabKey.METRICS_TABLE && !R.isNil(tablePreset))) &&
      fetchKey &&
      dataStatusMap[fetchKey] === 'readyToFetch'
    ) {
      if (R.isNil(dataMap[fetchKey])) {
        try {
          fetchPerformanceData(fetchKey, dates);
        } catch {
          console.error(
            `Failed to fetch performance data for ${company}, ${fetchKey}, ${dates}. Refresh to retry`
          );
          setDataStatusMap(fetchKey, 'failed');
        }
      }
      setDataStatusMap(fetchKey, 'fetched');
    }
  }, [
    company,
    dataMap,
    dataStatusMap,
    dates,
    fetchPerformanceData,
    fetchKey,
    setDataStatusMap,
    selectedTab,
    tablePreset
  ]);

  useEffect(() => {
    if (
      (selectedTab === TabKey.OVERVIEW ||
        (selectedTab === TabKey.METRICS_TABLE && !R.isNil(tablePreset))) &&
      otherFetchKey &&
      dataStatusMap[otherFetchKey] === 'readyToFetch'
    ) {
      if (R.isNil(dataMap[otherFetchKey])) {
        try {
          fetchPerformanceData(otherFetchKey, otherDates);
        } catch {
          console.error(
            `Failed to fetch performance data for ${company}, ${otherFetchKey}, ${otherDates}. Refresh to retry`
          );
          setDataStatusMap(otherFetchKey, 'failed');
        }
      }
      setDataStatusMap(otherFetchKey, 'fetched');
    }
  }, [
    company,
    dataMap,
    dataStatusMap,
    fetchPerformanceData,
    otherDates,
    otherFetchKey,
    selectedTab,
    setDataStatusMap,
    tablePreset
  ]);

  useEffect(() => {
    if (fetchKey && R.isNil(dataStatusMap[fetchKey]) && !data) {
      setDataStatusMap(fetchKey, 'readyToFetch');
    }
  }, [
    branchBuild,
    company,
    data,
    dataStatusMap,
    dates,
    fetchPerformanceData,
    fetchKey,
    fetchKPI,
    fetchLag,
    setDataMap,
    setDataStatusMap,
    setError
  ]);

  useEffect(() => {
    if (otherFetchKey && R.isNil(dataStatusMap[otherFetchKey]) && !otherData) {
      setDataStatusMap(otherFetchKey, 'readyToFetch');
    }
  }, [
    branchBuild,
    company,
    data,
    dataStatusMap,
    dates,
    fetchPerformanceData,
    fetchKey,
    fetchKPI,
    fetchLag,
    otherData,
    otherFetchKey,
    setDataMap,
    setDataStatusMap,
    setError
  ]);

  const getCustomMetrics = useCallback(() => {
    (async () => {
      const customMetricsResp = await SingleChannelLambdaFetch<GetCustomMetricsParams>(
        '/getCustomMetrics',
        {
          params: {
            company,
            channel: prefix
          }
        }
      );
      const customMetrics = await awaitJSON<GetCustomMetricsResponse>(customMetricsResp);
      const builtCustomColumnMetaDataMap: Record<string, ColumnMetaData> = {};
      for (const metric of customMetrics) {
        const convertedMetric = convertCustomMetric(metric);
        if (convertedMetric) {
          builtCustomColumnMetaDataMap[metric.name] = convertedMetric;
        }
      }
      setCustomColumnMetaDataMap(builtCustomColumnMetaDataMap);
    })();
  }, [company, prefix]);

  useEffect(() => {
    (async () => {
      if (R.isNil(customColumnMetaDataMap)) {
        await getCustomMetrics();
      }
    })();
  }, [customColumnMetaDataMap, getCustomMetrics]);

  const savePagePresetToDB = useCallback(
    async (pagePresetToSave: MetricsPagePreset) => {
      // Lag channels: no lag means Default, which is saved as the window it resolved to.
      const newPagePreset =
        pagePresetToSave && isLagChannel && !pagePresetToSave.globalOptions?.lag
          ? {
              ...pagePresetToSave,
              globalOptions: { ...pagePresetToSave.globalOptions, lag: defaultLagWindow }
            }
          : pagePresetToSave;
      if (newPagePreset) {
        if (!newPagePreset.temporary && newPagePreset.name === '') {
          setError({
            title: 'Blank Name',
            message: 'Your view must have a name.'
          });
          return;
        }
        setSaving(true);
        const body: SavePagePresetParams = {
          company: globalBrand || company,
          companyToUseInDB: company,
          group: newPagePreset.presetGroup,
          id: newPagePreset.id ? newPagePreset.id : 'new',
          mediatype: prefix,
          name: newPagePreset.name,
          preset: newPagePreset,
          temporary: newPagePreset.temporary
        };
        try {
          const response = await savePagePreset(body);
          if ('message' in response) {
            throw new Error(response.message);
          }
          const { id, name } = response;
          const newPath = `${channelBasePath}/${selectedTab}${setPresetSearchParams(
            location.search,
            { pagePresetName: name }
          )}`;
          if (pagePreset && newPagePreset.id === pagePreset.id) {
            navigate(newPath);
          }
          if (
            (pagePreset && newPagePreset.id === pagePreset.id) ||
            body.id === 'new' ||
            body.id <= 0
          ) {
            setPagePresetChanges(newPagePreset);
            setURL(name);
          }
          setDataMap(fetchKey, undefined);
          setDataStatusMap(fetchKey, undefined);
          setPagePresets(presets => [
            ...R.defaultTo([], presets),
            {
              ...newPagePreset,
              id,
              name,
              temporary: Boolean(body.temporary)
            }
          ]);
        } catch (e) {
          setError({
            title: "Couldn't save view",
            message: e.message,
            reportError: e
          });
        } finally {
          setSaving(false);
        }
      }
    },
    [
      location.search,
      isLagChannel,
      defaultLagWindow,
      globalBrand,
      company,
      prefix,
      setError,
      savePagePreset,
      navigate,
      channelBasePath,
      selectedTab,
      pagePreset,
      setURL,
      setDataMap,
      fetchKey,
      setDataStatusMap,
      setPagePresets
    ]
  );

  /// /////////////////////////
  // PAGE CONTROLS
  /// /////////////////////////
  const pagePresetPicker = (
    <PresetDropdown
      size={Size.MD}
      addPresetOnClick={() => {
        setShowNewPagePresetModal(true);
        // In the case that we are in the standard group already, clear this out
        setPagePresetChanges(
          preset =>
            ({
              ...preset,
              id: 'new',
              name: 'New Page Preset!',
              presetGroup: '',
              dateInfo: {
                ...preset.dateInfo,
                dateType: 'relative',
                comparison_dateType: 'relative',
                relative_date_info: 'Last Month',
                comparison_relative_date_info: 'Default'
              },
              globalOptions: {
                ...R.defaultTo({}, preset.globalOptions),
                admin_lock: false,
                role: 10,
                isDefault: false
              }
            }) as any
        );
      }}
      addPresetText='Add New Page Preset'
      applyOnClick={preset => {
        setURL(preset.name);
        resetPickers();
      }}
      cancelOnClick={() => {}}
      className={`pagePresetPicker ${fetchingPagePresets ? 'loading' : ''}`}
      deleteItemOnClick={presetID => deletePagePreset({ id: presetID, mediatype: prefix })}
      duplicateItemOnClick={preset =>
        confirmSave(() =>
          savePagePreset({
            company,
            id: 'new',
            mediatype: prefix,
            preset: {
              ...preset,
              globalOptions: {
                ...R.defaultTo({}, preset.globalOptions),
                admin_lock: false
              }
            },
            name: `${preset.name} (Copy)`,
            group: preset.presetGroup
          })
        )
      }
      editItemOnClick={preset => {
        const presetCopy = { ...preset };
        if (
          preset.dateInfo &&
          preset.dateInfo.dateType === 'relative' &&
          !preset.dateInfo.relative_date_info
        ) {
          presetCopy.dateInfo.dateType = 'exact';
        }
        if (preset.dateInfo && !preset.dateInfo.comparison_relative_date_info) {
          presetCopy.dateInfo.comparison_dateType = 'exact';
        }
        setPagePresetChanges(presetCopy);
        setShowEditPagePresetModal(true);
      }}
      hasPresetGroups
      hasTicks
      label={
        pagePreset && !R.isNil(pagePreset.id)
          ? `Page Preset: ${pagePreset.temporary ? 'Temp' : pagePreset.name}`
          : 'Load Page Preset'
      }
      presets={R.filter(
        preset => !preset.temporary || (!R.isNil(pagePreset) && preset.id === pagePreset.id),
        pagePresets
      )}
      renameItemOnClick={preset => {
        setPagePresetChanges(preset);
        setShowRenamePagePresetModal(true);
      }}
      selectedPreset={pagePreset}
      setAsDefault={preset =>
        confirmSave(async () => {
          const res = await savePagePreset({
            company,
            id: R.defaultTo('new', preset.id),
            mediatype: prefix,
            preset: {
              ...preset,
              globalOptions: { ...R.defaultTo({}, preset?.globalOptions), isDefault: true }
            },
            name: preset.name,
            group: preset.presetGroup
          });
          if (res && 'id' in res) {
            setError({
              message: `${preset.name} (${res.id}) has been set as the default ${prefix} preset.`,
              title: 'Success!',
              intent: 'success'
            });
          }
        })
      }
      subTitles={['Preset Group', 'Preset']}
    />
  );

  const dimensionOptionsByName = useMemo(
    () => Object.values(dimensionColumnMetaDataMap).map(col => col?.name || ''),
    [dimensionColumnMetaDataMap]
  );

  // data col / metric option names — granularity-restricted data cols (e.g. campaign-only
  // impression_share) must be checked against the filter too, not just dimensions
  const dataColOptionsByName = useMemo(() => Object.keys(columnMetaDataMap), [columnMetaDataMap]);

  const tablePresetCols = useMemo(
    () => [
      ...(tablePreset?.dimensionColumns.map(info => info.dimensionVarName) || []),
      // table data cols can be granularity-restricted too (e.g. campaign-only
      // impression_share), so they constrain which filter dims are valid
      ...(tablePreset?.dataColumns.map(col => col.dataVarName) || [])
    ],
    [tablePreset]
  );

  const activeFilterDims = useMemo(
    () => (activeFilterStateOrPreset ? extractDimsFromFilterPreset(activeFilterStateOrPreset) : []),
    [activeFilterStateOrPreset]
  );

  // keyed by dim AND data col option names, so the filter disables both incompatible
  // table dimensions and incompatible metrics (e.g. campaign-only impression_share)
  const incompatibleTableDimsFromFilter = useMemo(
    () =>
      getInvalidGranularityDetailsFromPreset(
        activeFilterStateOrPreset,
        [...dimensionOptionsByName, ...dataColOptionsByName],
        prefix
      ),
    [activeFilterStateOrPreset, dimensionOptionsByName, dataColOptionsByName, prefix]
  );

  const incompatibleDeliveryDimsFromFilter = useMemo(() => {
    const result: InvalidGranularityDetails = {};
    for (const [prettyName, varName] of Object.entries(deliveryDimensionMap)) {
      if (incompatibleTableDimsFromFilter[varName]) {
        result[prettyName] = incompatibleTableDimsFromFilter[varName];
      }
    }
    return result;
  }, [incompatibleTableDimsFromFilter, deliveryDimensionMap]);

  // the page preset modal's chosen table preset cols, which disable incompatible filter presets there
  const tableColsFromTableChanges = useMemo(() => {
    const currentPresetChangesTablePreset = tablePresets.find(
      t => t.id === pagePresetChanges.globalOptions.tableID
    );
    if (!currentPresetChangesTablePreset?.dimensionsByName) {
      return undefined;
    }
    return [
      ...currentPresetChangesTablePreset.dimensionsByName,
      ...(currentPresetChangesTablePreset.dataColsByName || [])
    ];
  }, [pagePresetChanges, tablePresets]);

  // the page preset modal's chosen filter preset dims, which disable incompatible table presets there
  const filterDimsFromFilterChanges = useMemo(() => {
    const currentPresetChangesFilterPreset = filters?.find(
      f => f.id === pagePresetChanges.filterInfo?.filterID
    );
    return currentPresetChangesFilterPreset
      ? extractDimsFromFilterPreset(currentPresetChangesFilterPreset)
      : undefined;
  }, [filters, pagePresetChanges]);

  const incompatibleDeliveryDimsFromFilterChanges = useMemo(() => {
    const currentPresetChangesFilterPreset = filters?.find(
      f => f.id === pagePresetChanges.filterInfo?.filterID
    );
    if (currentPresetChangesFilterPreset) {
      const deliveryVarNames = Object.values(deliveryDimensionMap);
      const varNameKeyed = getInvalidGranularityDetailsFromPreset(
        currentPresetChangesFilterPreset,
        deliveryVarNames,
        prefix
      );
      const result: InvalidGranularityDetails = {};
      for (const [prettyName, varName] of Object.entries(deliveryDimensionMap)) {
        if (varNameKeyed[varName]) {
          result[prettyName] = varNameKeyed[varName];
        }
      }
      return result;
    }
    return {};
  }, [deliveryDimensionMap, filters, pagePresetChanges, prefix]);

  const incompatibleFilterDimsFromDelivery = useMemo(() => {
    const deliveryVarName = deliveryDimensionMap[deliveryDimension];
    if (!deliveryVarName) {
      return {};
    }
    return getInvalidGranularityDetails([deliveryVarName], dimensionOptionsByName, prefix);
  }, [deliveryDimension, deliveryDimensionMap, dimensionOptionsByName, prefix]);

  const incompatibleFilterDimsFromDeliveryChanges = useMemo(() => {
    const deliveryVarName =
      deliveryDimensionMap[pagePresetChanges.globalOptions?.deliveryDimension || ''];
    if (!deliveryVarName) {
      return {};
    }
    return getInvalidGranularityDetails([deliveryVarName], dimensionOptionsByName, prefix);
  }, [pagePresetChanges, deliveryDimensionMap, dimensionOptionsByName, prefix]);

  const kpiData = useKpiPickerData({
    company,
    mediaType: prefix,
    useCrossChannelKpis,
    useTestParams: true
  });

  const kpiOptions = useMemo(() => {
    const computedKpiOptions: {
      label: string;
      value: string;
      trailingContent?: ReactElement;
    }[] = convertKPIMapToOptions(prefix, R.defaultTo({ kpiMap: {} }, kpiData).kpiMap);
    for (const option of computedKpiOptions) {
      // FOR TINUITI LIVE DO NOT REMOVE 5/14
      if (company === 'tinuititest' && prefix === YOUTUBE && option.value === 'Favor Paid') {
        option.label = 'Purchases';
      }
      if (kpiMetaData[option.value]) {
        const { info } = kpiMetaData[option.value] as P.KpiMetaData & { info: string };
        if (info) {
          option.trailingContent = (
            <OverlayTrigger
              overlay={<Tooltip id={uuid.v4()}>{info}</Tooltip>}
              placement='bottom-start'
            >
              <div className='kpi-info-tooltip'>
                <MdInfoOutline />
              </div>
            </OverlayTrigger>
          );
        }
      }
    }
    return R.sortBy(R.prop('label'), R.uniq(computedKpiOptions));
  }, [kpiData, kpiMetaData, prefix, company]);

  const pickerKPIName = useMemo(() => {
    const kpiOption = R.find(option => option.value === pickerKPI, kpiOptions);
    return kpiOption ? kpiOption.label : '';
  }, [kpiOptions, pickerKPI]);

  const kpiPicker = (
    <Dropdown
      label='KPI'
      size={Size.MD}
      onChange={option => {
        Mixpanel.track(MxE.PageKpiChange, {
          widget: 'kpi',
          option_selected: option
        });
        setPickerKPI(option);
      }}
      options={kpiOptions}
      value={pickerKPI}
    />
  );
  // Commerce offers only 14d.
  const lagPicker = (isLagChannel || prefix === 'commerce') && (
    <Dropdown
      label='Lag'
      size={Size.MD}
      onChange={setPickerLag}
      options={getLagPickerOptions(defaultLagWindow).filter(
        option => prefix !== 'commerce' || option.value === '14d'
      )}
      placeholder='Default'
      value={shownLag}
    />
  );
  const audiencePicker = isQueryArgOnChannel('audience', prefix) && (
    <Dropdown
      label='Audience'
      size={Size.MD}
      onChange={setPickerAudience}
      options={LINEAR_AUDIENCES}
      value={fetchAudience}
    />
  );

  const sourcePicker =
    selectedSources && sourceOptions && setSelectedSources ? (
      <SourcePicker
        selectedSources={selectedSources}
        sourceOptions={sourceOptions}
        applyOnClick={setSelectedSources}
        size={Size.MD}
        platforms={platformOptions || []}
        invalidAnalyticDetailsTable={
          // wait to load preset before enableing analytics
          tablePreset
            ? getInvalidAnalyticsDetails(tablePreset, true, expAnalyticsCols, 'table')
            : [true, []]
        }
        invalidAnalyticDetailsFilter={getInvalidAnalyticsDetails(
          activeFilterStateOrPreset,
          true,
          expAnalyticsCols,
          'filter'
        )}
      />
    ) : null;

  const actionsContainer = (
    <Toolbar className='performance-actions performance-title'>
      {pagePresetPicker}
      {sourcePicker}
      {kpiPicker}
      {lagPicker}
      {audiencePicker}
      {localStorage.getItem(dateRangeKey) && sourcePicker === null && (
        <Button size={Size.MD} onClick={resetDefaultDates}>
          Reset Default Dates
        </Button>
      )}
      <DatePicker
        range={dates}
        size={Size.MD}
        onChange={onDatePickerChange}
        datePresets
        comparison
        comparisonRange={otherDates}
        comparisonOnChange={onOtherDatePickerChange}
        additionalRightHeaderItems={
          shouldEnableImpsBetweenToggle && isInternal && STREAMING_PLUS.includes(prefix) ? (
            <SegmentedControlButton
              background={Background.LIGHT}
              size={Size.MD}
              isExclusiveSelect
              options={
                shouldEnableByResponseDate
                  ? ['Impressions Served Between', 'Response Between']
                  : ['Impressions Served Between']
              }
              selectedOption={
                datePickerImpsBetween ? 'Impressions Served Between' : 'Response Between'
              }
              onChange={() => {
                setDatePickerImpsBetween(!datePickerImpsBetween);
              }}
            />
          ) : undefined
        }
        onApply={
          shouldEnableImpsBetweenToggle && isInternal
            ? () => {
                setImpsBetween(datePickerImpsBetween);
              }
            : undefined
        }
      />
    </Toolbar>
  );

  // Always render this wrapper (so <Page> reserves the sub-header's space from the first paint)
  // and only toggle visibility once pagePreset is ready, instead of mounting the row late and
  // shifting everything below it down — matches CrossChannel's subHeader pattern.
  const subHeader = (
    <div style={{ visibility: pagePreset ? 'visible' : 'hidden' }}>
      {pagePreset && (
        <FetchFilterBar
          categories={filterDataProcessor(
            filterData,
            tablePreset,
            deliveryDimension,
            selectedTab,
            pickerKPI
          )}
          groupedCategories={groupedSegments}
          company={company}
          filterID={filterID}
          filters={filters}
          filterSource={filterSource}
          platform={prefix}
          refreshData={() => {
            setDataMap(fetchKey, undefined);
            setDataStatusMap(fetchKey, 'readyToFetch');
            setDataStatusMap(otherFetchKey, 'readyToFetch');
            setDeliveryDataMap(deliveryFetchKey, undefined);
            setDeliveryDataStatusMap(deliveryFetchKey, 'readyToFetch');
            setDeliveryDataStatusMap(otherDeliveryFetchKey, 'readyToFetch');
          }}
          setFilterID={setFilterID}
          setFilters={setFilters}
          setFilterSource={
            setFilterSource as Dispatch<
              SetStateAction<'presetFilterID' | 'currentFilterID' | 'presetState' | 'currentState'>
            >
          }
          setState={setFilterState}
          state={filterState}
          analyticsIsSource={analyticsIsSource}
          incompatibleFilterDimsFromDelivery={incompatibleFilterDimsFromDelivery}
          tableCols={tablePresetCols}
          channel={prefix}
        />
      )}
    </div>
  );

  /// /////////////////////////
  // OVERVIEW TAB
  /// /////////////////////////
  // Turns `byDate=dateOnly` rows (one per day, or one per week/month/... bucket) into chart points.
  const toOverviewData = useCallback(
    (rows: S.GetPageDataResponse['data']): MetricTotalsByDate[] => {
      if (!customColumnMetaDataMap) return [];
      // We will use this to extract data from the fetch, since we don't have an actual column,
      // use all global values.
      const fakeColumn = { audience: fetchAudience, kpi: fetchKPI, lag: fetchLag };
      const metricFetchKey = dataFetchKey(fakeColumn);
      const computed: MetricTotalsByDate[] = [];
      for (const row of rows) {
        const date = (row.dimensions as Partial<Record<string, string>>).Date;
        // Skip missing or unparseable labels (e.g. the SQL 'Unknown' bucket); they'd sort as NaN.
        if (!date || !validDate(new Date(date))) {
          continue;
        }
        const metrics = {
          clicks: 0,
          clicksConversionRate: 0,
          cpc: 0,
          cpm: 0,
          cpx: 0,
          ctr: 0,
          impressions: 0,
          impressionShare: 0,
          impsConversionRate: 0,
          revenue: 0,
          roas: 0,
          spend: 0,
          spots: 0,
          topImpressionShare: 0,
          volume: 0
        };
        if (row.fetches && row.fetches[metricFetchKey] && validDate(new Date(date))) {
          for (const metric of R.keys(sparkChartMetricsMap)) {
            metrics[metric] = evaluateOverviewMetric(
              columnMetaDataMap[sparkChartMetricsMap[metric]],
              row.fetches[metricFetchKey],
              sparkChartMetricsMap[metric]
            );
          }
          for (const metric of R.keys(customColumnMetaDataMap)) {
            const customMeta = customColumnMetaDataMap[metric];
            metrics[metric] = evaluateOverviewMetric(
              customMeta,
              getRowAggregatorDatum(customMeta, row, fakeColumn as unknown as Column, dataFetchKey),
              metric
            );
          }
        }
        computed.push({ date, metrics });
      }
      return R.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(), computed);
    },
    [
      columnMetaDataMap,
      customColumnMetaDataMap,
      dataFetchKey,
      fetchAudience,
      fetchKPI,
      fetchLag,
      sparkChartMetricsMap
    ]
  );

  const [overviewData, otherOverviewData] = useMemo(() => {
    if (
      selectedTab !== TabKey.OVERVIEW ||
      !data?.data ||
      !otherData?.data ||
      !customColumnMetaDataMap
    ) {
      return [undefined, undefined];
    }
    setLoading(false);
    return [toOverviewData(data.data), toOverviewData(otherData.data)];
  }, [customColumnMetaDataMap, data, otherData, selectedTab, toOverviewData]);

  // The compare chart's Week/Month/... points come from the server, bucketed in SQL, so ratio and
  // custom metrics match the metrics table. Summing daily points client-side can't recompute them.
  const [compareGroupingKey, setCompareGroupingKey] = useState<DateGroupingKey>('Day');
  const compareParams = useMemo(
    () => [buildPerformanceParams(dates), buildPerformanceParams(otherDates)],
    [buildPerformanceParams, dates, otherDates]
  );
  // Without both ranges the lambda would fall back to its default dates (e.g. after resetPickers).
  const canFetchCompareGrouped =
    selectedTab === TabKey.OVERVIEW &&
    Boolean(customColumnMetaDataMap) &&
    Boolean(dates) &&
    Boolean(otherDates);
  const compareGroupedQuery = useQuery({
    meta: { suppressGlobalError: true },
    queryKey: [
      'singleChannelCompareGrouped',
      compareGroupingKey,
      // getPerformanceData adds sources itself, so buildPerformanceParams doesn't cover them.
      [...(selectedSources ?? [])].sort().join(','),
      compareParams
    ],
    queryFn: async () => {
      const responses = await Promise.all(
        compareParams.map(params =>
          getPerformanceData(
            {
              ...params,
              byDate: 'dateOnly',
              dateGrouping: compareGroupingKey.toLowerCase(),
              // The chart only reads the bucket rows, so skip the totals query.
              includeTotals: 'false'
            },
            { tablePreset }
          )
        )
      );
      const cometErrorMessage = responses.map(getCometErrorMessage).find(Boolean);
      if (cometErrorMessage) {
        throw new Error(cometErrorMessage);
      }
      return responses;
    },
    enabled: canFetchCompareGrouped && compareGroupingKey !== 'Day'
  });
  useEffect(() => {
    if (compareGroupedQuery.error) {
      setError({
        message: `Failed to load grouped compare data: ${compareGroupedQuery.error.message}`,
        reportError: compareGroupedQuery.error
      });
    }
  }, [compareGroupedQuery.error, setError]);
  const compareGroupedData = useMemo(() => {
    const responses = compareGroupedQuery.data;
    if (!responses || compareGroupingKey === 'Day') return undefined;
    return {
      groupingKey: compareGroupingKey,
      data: toOverviewData(responses[0].data),
      otherData: toOverviewData(responses[1].data)
    };
  }, [compareGroupedQuery.data, compareGroupingKey, toOverviewData]);

  const [overviewServerTotals, otherOverviewServerTotals] = useMemo(() => {
    const totalsKey = dataFetchKey({ audience: fetchAudience, kpi: fetchKPI, lag: fetchLag });
    const customMap = customColumnMetaDataMap ?? {};
    return [
      getSparkServerTotals(data?.totals?.[totalsKey], sparkChartMetricsMap, customMap),
      getSparkServerTotals(otherData?.totals?.[totalsKey], sparkChartMetricsMap, customMap)
    ];
  }, [
    customColumnMetaDataMap,
    data,
    dataFetchKey,
    fetchAudience,
    fetchKPI,
    fetchLag,
    otherData,
    sparkChartMetricsMap
  ]);

  const overviewSparkCharts = useMemo(() => {
    let index = -1;
    const metricOptions = R.map(
      key => ({
        label: R.defaultTo(sparkChartMetricsMap[key], sparkChartMetricPrettyNameMap[key]),
        value: key as any
      }),
      R.keys(sparkChartMetricsMap)
    );
    if (customColumnMetaDataMap) {
      for (const dataVarName of R.keys(R.defaultTo({}, customColumnMetaDataMap))) {
        metricOptions.push({
          label: customColumnMetaDataMap[dataVarName].prettyName,
          value: dataVarName
        });
      }
    }

    // Older saved presets store camelCase keys (e.g. 'volumeDirect') where the metric config now
    // uses snake_case ('volume_direct'); resolve them without touching the saved preset.
    const camelToConfigKey: Record<string, string> = {};
    for (const key of R.keys(sparkChartMetricsMap)) {
      camelToConfigKey[key.replace(/_([a-z])/g, (_match, char: string) => char.toUpperCase())] =
        key;
    }
    const resolveSparkMetric = (metric: string) =>
      sparkChartMetricsMap[metric] ? metric : (camelToConfigKey[metric] ?? metric);

    // In the DB, sparkChartTypes is an array of "Total" | "Avg" | false
    const sparkChartsConfig = pagePreset?.globalOptions?.sparkChartTypes || [
      'Total',
      'Total',
      'Total',
      'Total'
    ];

    return (
      pagePresetChanges &&
      pagePresetChanges.globalOptions &&
      customColumnMetaDataMap && (
        <div className='overview-spark-charts'>
          {R.filter(
            chart => !R.isNil(chart),
            R.map(
              savedMetric => {
                index += 1;
                const metric = resolveSparkMetric(savedMetric) as Metric;
                if (overviewData && otherOverviewData) {
                  return (
                    <SparkChart
                      data={overviewData}
                      defaultMetric={metric}
                      key={metric}
                      otherData={otherOverviewData}
                      metricOptions={metricOptions}
                      columnMetaDataMap={columnMetaDataMap}
                      formatter={metricFormatter}
                      defaultValueToggle={sparkChartsConfig[index] || 'Total'}
                      serverTotals={overviewServerTotals}
                      otherServerTotals={otherOverviewServerTotals}
                    />
                  );
                }
                return undefined;
              },
              R.defaultTo(
                defaultSparkChartMetrics,
                pagePresetChanges.globalOptions.sparkCharts
              ) as Metric[]
            )
          )}
        </div>
      )
    );
  }, [
    customColumnMetaDataMap,
    defaultSparkChartMetrics,
    otherOverviewData,
    otherOverviewServerTotals,
    overviewData,
    overviewServerTotals,
    pagePresetChanges,
    sparkChartMetricPrettyNameMap,
    sparkChartMetricsMap,
    pagePreset?.globalOptions?.sparkChartTypes,
    columnMetaDataMap,
    metricFormatter
  ]);

  const overviewCompareMetricsChart = overviewData && otherOverviewData && compareMetricOptions && (
    <div className='overview-compare-metrics-chart'>
      <CompareMetrics
        data={overviewData}
        dates={dates}
        otherData={otherOverviewData}
        otherDates={otherDates}
        defaultMetrics={
          pagePresetChanges &&
          pagePresetChanges.globalOptions &&
          pagePresetChanges.globalOptions &&
          pagePresetChanges.globalOptions.compareMetrics
            ? pagePresetChanges.globalOptions.compareMetrics
            : defaultCompareMetrics || []
        }
        defaultIncludeOtherDates={
          pagePresetChanges &&
          pagePresetChanges.globalOptions &&
          pagePresetChanges.globalOptions &&
          pagePresetChanges.globalOptions.compareMetricsIncludeOtherDates
            ? pagePresetChanges.globalOptions.compareMetricsIncludeOtherDates
            : false
        }
        dateIncrementOptions={R.map(option => DATE_GROUPING_MAP[option], dateIncrementOptions)}
        defaultDateGroupingKey={
          pagePresetChanges && pagePresetChanges.dateInfo && pagePresetChanges.dateInfo.increments
            ? DATE_GROUPING_MAP[pagePresetChanges.dateInfo.increments]
            : undefined
        }
        metricOptions={compareMetricOptions}
        formatter={metricFormatter}
        serverGroupedData={compareGroupedData}
        onGroupingKeyChange={setCompareGroupingKey}
        serverGroupedUnavailable={Boolean(compareGroupedQuery.error) || !canFetchCompareGrouped}
      />
    </div>
  );

  // Only relevant for Linear
  const [liftFromTvDataMap, setLiftFromTvDataMap] = useState({});
  const liftFromTvKey = useMemo(() => {
    if (company && fetchKPI && dates) {
      return `${company}_${fetchKPI}_${dates.start}_${dates.end}`;
    }
    return undefined;
  }, [company, fetchKPI, dates]);

  useEffect(() => {
    (async () => {
      if (prefix === LINEAR && fetchKPI && dates && liftFromTvKey) {
        try {
          const res = await LinearPerformanceLambdaFetch('/getLiftFromTvChart', {
            params: {
              kpi: fetchKPI,
              company,
              start: dates.start,
              end: dates.end
            }
          });
          const liftFromTvData = await awaitJSON(res);
          setLiftFromTvDataMap(map => ({ ...map, [liftFromTvKey]: liftFromTvData }));
        } catch (e) {
          setError({
            title: "Couldn't load lift from TV data",
            message: e.message,
            reportError: e
          });
        }
      }
    })();
  }, [company, dates, fetchKPI, liftFromTvKey, prefix, setError]);

  const liftFromTvChart = useMemo(() => {
    if (prefix === LINEAR && liftFromTvKey && liftFromTvDataMap[liftFromTvKey]) {
      return <LiftFromTvChart data={liftFromTvDataMap[liftFromTvKey]} fiscalCal={fiscalCal} />;
    }
    return undefined;
  }, [liftFromTvDataMap, liftFromTvKey, prefix, fiscalCal]);

  const residentialResponseRates = useMemo(() => {
    if (data) {
      const residentialFetchKey = dataFetchKey({
        audience: fetchAudience,
        kpi: fetchKPI,
        lag: fetchLag
      });

      let totalResponses = 0;
      let totalResidentialResponses = 0;
      let totalImps = 0;
      let totalResidentialImps = 0;

      for (const item of data.data) {
        if (item.fetches) {
          const obj = item?.fetches[residentialFetchKey];

          if (obj) {
            totalResponses += (obj || {}).resps || 0;
            totalResidentialResponses += (obj || {}).residResps || 0;
            totalImps += (obj || {}).imps || 0;
            totalResidentialImps += (obj || {}).residImps || 0;
          }
        }
      }

      const residentialResponseRate = formatPercent(totalResidentialResponses / totalResponses, 1);
      const residentialDeliveryRate = formatPercent(totalResidentialImps / totalImps, 1);

      return {
        residentialResponseRate,
        residentialDeliveryRate
      };
    }
    return undefined;
  }, [data, dataFetchKey, fetchAudience, fetchKPI, fetchLag]);

  // Only relevant for Streaming and Audio
  const conversionLatencyChart = useMemo(() => {
    if ((prefix === STREAMING_PLUS[0] || prefix === STREAMING_PLUS[1]) && fetchKPI && dates) {
      return (
        <div className='conversion-latency-container'>
          <ConversionLatencyHistogram
            company={company}
            kpi={fetchKPI}
            start={dates.start}
            end={dates.end}
            prefix={prefix as 'streaming' | 'audio'}
            isGraph={isGraph}
          />
          <MatchableRates residentialResponseRates={residentialResponseRates} />
        </div>
      );
    }
    return undefined;
  }, [company, dates, fetchKPI, isGraph, prefix, residentialResponseRates]);

  /// /////////////////////////
  // METRICS TAB
  /// /////////////////////////
  // Check to make sure nothing is missing from the table preset
  const customSegmentColumnMap = useMemo(() => {
    if (!R.isNil(filterData) && !R.isEmpty(filterData)) {
      const customSegments = filterData
        .filter(option => option.isCustomSegment)
        .reduce(
          (curr, prev) => ({
            ...curr,
            [prev.label]: { contentType: 'text', name: prev.label, prettyName: prev.label }
          }),
          {}
        );
      return customSegments;
    }
    return undefined;
  }, [filterData]);

  const processedTablePreset: MetricsTablePreset | undefined = useMemo(() => {
    const dimensionColumns: DimensionColumn[] = [];
    const dataColumns: Column[] = [];
    if (pagePreset && tablePreset && customSegmentColumnMap && columnMetaDataMap) {
      R.map(oldDimCol => {
        const dimColMetaDataMap = {
          ...R.defaultTo({}, customSegmentColumnMap),
          ...dimensionColumnMetaDataMap
        };
        const isGroupedSegment = Boolean(
          groupedSegments &&
          Object.keys(groupedSegments).includes(oldDimCol.dimensionVarName) &&
          shouldEnableMetricsTableSegmentGroups
        );
        let columnMetaData: any;
        if (isGroupedSegment) {
          columnMetaData = {
            name: oldDimCol.dimensionVarName,
            prettyName: oldDimCol.dimensionVarName,
            contentType: 'text',
            iconStyle: undefined
          };
        } else if (oldDimCol && dimColMetaDataMap[oldDimCol.dimensionTypeName]) {
          columnMetaData = dimColMetaDataMap[oldDimCol.dimensionTypeName];
        } else {
          if (oldDimCol) {
            console.error(
              `${oldDimCol.dimensionTypeName} is not present in the dimension column metadata map`
            );
          }
          // Column not found in the metadata map -- drop it instead of rendering a fallback column.
          return oldDimCol;
        }
        const dimensionTypeName = isGroupedSegment
          ? oldDimCol.dimensionVarName
          : oldDimCol.dimensionTypeName;
        const icon =
          oldDimCol.icon !== undefined ? oldDimCol.icon : defaultIconFor(columnMetaData.iconStyle);
        const newDimCol: DimensionColumn = {
          ...oldDimCol,
          dimensionTypeName,
          dimensionVarName: columnMetaData.name ?? oldDimCol.dimensionVarName,
          label: oldDimCol && oldDimCol.label ? oldDimCol.label : columnMetaData.prettyName,
          role: oldDimCol.role ? oldDimCol.role : 10,
          icon
        };
        dimensionColumns.push(newDimCol);
        return oldDimCol;
      }, tablePreset.dimensionColumns);
      R.map(oldDataCol => {
        if (!(oldDataCol && columnMetaDataMap[oldDataCol.dataVarName])) {
          if (oldDataCol) {
            console.error(`${oldDataCol.dataVarName} is not present in the column metadata map`);
          }
          // Column not found in the metadata map -- drop it instead of rendering a fallback column.
          return oldDataCol;
        }
        const dataColumnMetaData = columnMetaDataMap[oldDataCol.dataVarName];
        const newDataCol: Column = {
          ...oldDataCol,
          dataVarName: oldDataCol.dataVarName,
          label: oldDataCol.label ? oldDataCol.label : dataColumnMetaData.prettyName,
          role: oldDataCol.role ? oldDataCol.role : 10,
          kpi: oldDataCol.kpi ? oldDataCol.kpi : fetchKPI,
          // In memory only (not saved): the key needs the raw lag, which can be `default`.
          lag: (oldDataCol.lag ? oldDataCol.lag : fetchLag) as S.LagOption,
          // No fallback: audience isn't in dataFetchKey, and undefined means 'Global'.
          audience: oldDataCol.audience
        };
        dataColumns.push(newDataCol);
        return oldDataCol;
      }, tablePreset.dataColumns);
      const computedProcessedTablePreset = {
        ...tablePreset,
        dataColumns,
        dimensionColumns,
        globalOptions: {
          ...pagePreset.globalOptions,
          kpi: fetchKPI,
          lag: fetchLag,
          audience: fetchAudience
        }
      };
      return computedProcessedTablePreset;
    }
    return undefined;
  }, [
    columnMetaDataMap,
    customSegmentColumnMap,
    dimensionColumnMetaDataMap,
    fetchAudience,
    fetchKPI,
    fetchLag,
    pagePreset,
    tablePreset,
    shouldEnableMetricsTableSegmentGroups,
    groupedSegments
  ]);

  const comparisonPresetToUse = useMemo(
    () =>
      showMetricsTableComparison && processedTablePreset
        ? createComparisonTablePreset(processedTablePreset)
        : processedTablePreset,
    [processedTablePreset, showMetricsTableComparison]
  );

  const [dataWithAllDims, otherDataWithAllDims] = useMemo(() => {
    // Only the metrics table fills in missing dimensions, so skip the clone on other tabs.
    if (selectedTab !== TabKey.METRICS_TABLE) {
      return [data, otherData];
    }
    const clonedDataWithAllDims = R.clone(metricsTableData);
    const clonedOtherDataWithAllDims = R.clone(otherMetricsTableData);
    if (clonedDataWithAllDims && processedTablePreset) {
      for (const row of clonedDataWithAllDims.data) {
        for (const col of processedTablePreset.dimensionColumns) {
          if (R.isNil(row.dimensions[col.dimensionTypeName])) {
            const dimensionData = row.dimensions as DimensionMap;
            const cell = getDimensionCell(dimensionData, col);
            row.dimensions[col.dimensionTypeName] = cell.label;
          }
        }
      }
    }
    if (clonedOtherDataWithAllDims && processedTablePreset) {
      for (const row of clonedOtherDataWithAllDims.data) {
        for (const col of processedTablePreset.dimensionColumns) {
          if (R.isNil(row.dimensions[col.dimensionTypeName])) {
            const dimensionData = row.dimensions as DimensionMap;
            const cell = getDimensionCell(dimensionData, col);
            row.dimensions[col.dimensionTypeName] = cell.label;
          }
        }
      }
    }
    return [clonedDataWithAllDims, clonedOtherDataWithAllDims];
  }, [
    data,
    getDimensionCell,
    metricsTableData,
    otherData,
    otherMetricsTableData,
    processedTablePreset,
    selectedTab
  ]);

  const joinedData = useMemo(
    () =>
      dataWithAllDims &&
      dataWithAllDims.data &&
      otherDataWithAllDims &&
      otherDataWithAllDims.data &&
      selectedTab === TabKey.METRICS_TABLE
        ? joinMetricsTableData(
            dataWithAllDims.data,
            otherDataWithAllDims.data,
            dates?.start,
            dates?.end,
            otherDates?.start,
            otherDates?.end,
            tablePreset
          )
        : undefined,
    [dataWithAllDims, dates, otherDataWithAllDims, otherDates, selectedTab, tablePreset]
  );

  // Page Modals
  const editPagePresetModal = (showEditPagePresetModal || showNewPagePresetModal) && (
    <EditPagePresetModal
      dateIncrementOptions={dateIncrementOptions}
      deliveryDimensionMap={deliveryDimensionMap}
      deliveryMetricOptions={deliveryMetricOptions}
      filterPresets={filters}
      pagePresetChanges={pagePresetChanges}
      globalOptionsPickers={{
        audience: isQueryArgOnChannel('audience', prefix)
          ? ([...LINEAR_AUDIENCES] as any[])
          : undefined,
        kpi: { options: kpiOptions },
        lag: isLagChannel,
        impsBetween: STREAMING_PLUS.includes(prefix),
        source: sourceOptions || undefined
      }}
      groups={pagePresetGroups}
      mediatype={prefix}
      setPagePresetChanges={setPagePresetChanges}
      defaultLagWindow={defaultLagWindow}
      saveAndApplyOnClick={presetChanges => {
        confirmSave(
          () => {
            (async () => {
              const updatedPresetChanges =
                !R.isNil(presetChanges) &&
                !R.isNil(presetChanges.filterInfo) &&
                !R.isNil(presetChanges.filterInfo.filterState)
                  ? {
                      ...presetChanges,
                      filterInfo: { ...presetChanges.filterInfo, filterState }
                    }
                  : presetChanges;
              await savePagePresetToDB(updatedPresetChanges);
              if (
                updatedPresetChanges.temporary ||
                (updatedPresetChanges.id as any) === 'new' ||
                updatedPresetChanges.id <= 0 ||
                (pagePreset && updatedPresetChanges.id === pagePreset.id)
              ) {
                resetPickers();
              }
              setShowEditPagePresetModal(false);
              setShowNewPagePresetModal(false);
            })();
          },
          { temporary: presetChanges.temporary }
        );
      }}
      sparkChartOptions={R.map(
        metric => ({
          label: sparkChartMetricPrettyNameMap[metric],
          value: metric
        }),
        R.keys(sparkChartMetricPrettyNameMap)
      )}
      tablePresets={R.filter(candidatePreset => !candidatePreset.temporary, tablePresets)}
      onHide={() => {
        setShowEditPagePresetModal(false);
        setShowNewPagePresetModal(false);
        if (pagePreset) {
          setPagePresetChanges(pagePreset);
        }
      }}
      tableColsFromTableChanges={tableColsFromTableChanges}
      filterDimsFromFilterChanges={filterDimsFromFilterChanges}
      incompatibleDeliveryDimsFromFilterChanges={incompatibleDeliveryDimsFromFilterChanges}
      incompatibleFilterDimsFromDeliveryChanges={incompatibleFilterDimsFromDeliveryChanges}
    />
  );

  const renamePagePresetModal = showRenamePagePresetModal && (
    <RenameModal
      applyOnClick={newName => {
        confirmSave(() => {
          (async () => {
            await savePagePreset({
              company,
              id: pagePresetChanges.id,
              mediatype: prefix,
              name: newName,
              group: pagePresetChanges.presetGroup
            });
            setShowRenamePagePresetModal(false);
            setURL(newName, tablePresetChanges?.name);
          })();
        });
      }}
      applyText='Save'
      cancelText='Discard'
      disabled={pagePresetChanges.globalOptions.admin_lock && !isAdmin}
      onCancel={() => {
        setShowRenamePagePresetModal(false);
        if (pagePreset) {
          setPagePresetChanges(pagePreset);
        }
      }}
      onHide={() => {
        setShowRenamePagePresetModal(false);
        if (pagePreset) {
          setPagePresetChanges(pagePreset);
        }
      }}
      placeholder='Enter new preset name'
      subTitle='New Page Preset Name'
      title={`Rename ${pagePresetChanges && pagePresetChanges.name ? pagePresetChanges.name : ''}`}
    />
  );

  // Table Modals
  const defaultTablePresetNames = R.map(R.toLower, R.keys(deliveryDimensionMap));
  const editTablePresetModal = (showEditTablePresetModal || showNewTablePresetModal) && (
    <EditTablePresetModal
      defaultLagWindow={defaultLagWindow}
      columnMetaDataMap={columnMetaDataMap}
      defaultPresetNames={defaultTablePresetNames}
      dimensionColumnMetaDataMap={{
        ...R.defaultTo({}, customSegmentColumnMap),
        ...dimensionColumnMetaDataMap
      }}
      kpiMetaData={kpiData.kpiMap}
      prefix={prefix}
      onHide={() => {
        setShowEditTablePresetModal(false);
        setShowNewTablePresetModal(false);
        if (tablePreset) {
          setTablePresetChanges(tablePreset);
        }
      }}
      analyticsIsSource={analyticsIsSource}
      presetChanges={tablePresetChanges}
      refreshCustomMetrics={getCustomMetrics}
      setPresetChanges={setTablePresetChanges}
      saveAndApplyOnClick={newTablePreset => {
        confirmSave(
          () => {
            (async () => {
              const newID = await saveTablePreset({
                company,
                companyToUseInDB: globalBrand || company,
                id:
                  !R.isNil(newTablePreset.id) && newTablePreset.id > 0 ? newTablePreset.id : 'new',
                name: newTablePreset.name,
                mediatype: prefix,
                preset: newTablePreset,
                temporary: newTablePreset.temporary
              });
              setTablePresetMap(`${newID.id}`, {
                ...tablePresetChanges,
                ...newTablePreset,
                name: newID.name,
                id: newID.id,
                temporary: newTablePreset.temporary
              });
              if (
                pagePreset &&
                ((newTablePreset.id as any) === 'new' ||
                  newTablePreset.id === tablePresetID ||
                  newTablePreset.temporary)
              ) {
                const resolvedTablePresetName =
                  newTablePreset.temporary &&
                  (R.isNil(newTablePreset.id) ||
                    (newTablePreset.id as any) === 'new' ||
                    newTablePreset.id <= 0)
                    ? newID.name
                    : newTablePreset.name;
                setURL(pagePreset.name, resolvedTablePresetName);
                setDataMap(fetchKey, undefined);
                setDataStatusMap(fetchKey, undefined);
              }
              setShowEditTablePresetModal(false);
              setShowNewTablePresetModal(false);
            })();
          },
          { temporary: newTablePreset.temporary }
        );
      }}
      groupedSegments={groupedSegments}
      filterDims={activeFilterDims}
    />
  );
  const renameTablePresetModal = showRenameTablePresetModal && (
    <RenameModal
      applyOnClick={newName => {
        confirmSave(
          () => {
            (async () => {
              await saveTablePreset({
                company,
                id: tablePresetChanges.id,
                mediatype: prefix,
                name: newName,
                temporary: R.defaultTo(false, tablePresetChanges.temporary)
              });
              const presetInMap = tablePresetMap[`${tablePresetChanges.id}`];
              if (presetInMap) {
                setTablePresetMap(`${tablePresetChanges.id}`, {
                  ...presetInMap,
                  name: newName
                });
              }
              if (tablePresetChanges.id === tablePresetID) {
                setTablePresetChanges({ ...tablePresetChanges, name: newName });
              }
              setShowRenameTablePresetModal(false);
              if (pagePreset) {
                setURL(pagePreset.name, newName);
              }
            })();
          },
          { temporary: tablePresetChanges.temporary }
        );
      }}
      applyText='Save'
      cancelText='Discard'
      disabled={tablePresetChanges.admin_lock && !isAdmin}
      onCancel={() => {
        setShowRenameTablePresetModal(false);
        if (tablePreset) {
          setTablePresetChanges(tablePreset);
        }
      }}
      onHide={() => {
        setShowRenameTablePresetModal(false);
        if (tablePreset) {
          setTablePresetChanges(tablePreset);
        }
      }}
      placeholder='Enter new preset name'
      subTitle='New Table Preset Name'
      title={`Rename ${
        tablePresetChanges && tablePresetChanges.name ? tablePresetChanges.name : ''
      }`}
    />
  );

  // Table Sizing — matches CrossChannel's MetricsTableView (same measure-on-known-triggers
  // pattern; a ResizeObserver here creates a feedback loop against the table's own rendered
  // size and stabilizes too small, since the table's maxHeight prop is fed directly from this
  // measurement).
  useEffect(() => {
    let frame = 0;
    const windowResizeFunction = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setRerenderTable(true));
    };
    window.addEventListener('resize', windowResizeFunction);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', windowResizeFunction);
    };
  }, []);

  useLayoutEffect(() => {
    const newTableHeight = containerRef.current ? containerRef.current.offsetHeight : undefined;
    const newTableWidth = containerRef.current ? containerRef.current.offsetWidth : undefined;
    if (newTableHeight) {
      setTableHeight(newTableHeight);
    }
    if (newTableWidth) {
      setTableWidth(newTableWidth);
    }
    if (rerenderTable) {
      setRerenderTable(false);
    }
    // The truncated note takes space below the table, so remeasure when it appears. Setting
    // `rerenderTable` from a passive effect here instead can land in the same batch as this
    // reset, net to its current `true`, and never re-run this effect (table stuck on skeleton).
  }, [rerenderTable, metricsTableTruncated]);

  // Server totals cover every row the server returned, so they only replace the client's when the
  // in-table search reached the server; above the row cap the client only has the displayed rows.
  const tableServerValues = useMemo(
    (): ServerMetricValues => ({
      totals: tableFilterTerms ? data?.totals : undefined,
      otherTotals: tableFilterTerms ? otherData?.totals : undefined
    }),
    [data, otherData, tableFilterTerms]
  );

  // The controls (incl. the in-table search) must not depend on `rerenderTable`: it flips for one
  // layout pass after every fetch, and unmounting the FilterBar drops text typed but not yet entered.
  const hasMetricsTableData = Boolean(
    dataWithAllDims && tablePreset && processedTablePreset && customSegmentColumnMap
  );

  const metricsTable = useMemo(() => {
    if (
      dataWithAllDims &&
      !rerenderTable &&
      tablePreset &&
      processedTablePreset &&
      customSegmentColumnMap
    ) {
      const isLinear = prefix === LINEAR;
      const tilde = isLinear && shouldShowTilde(dates) ? '~' : undefined;
      const tildeRowPrefix = tilde ? () => tilde : undefined;

      return showMetricsTableComparison ? (
        joinedData && comparisonPresetToUse && (
          <MetricsTable
            columnMetaDataMap={{
              ...customSegmentColumnMap,
              ...columnMetaDataMap,
              ...dimensionColumnMetaDataMap
            }}
            data={R.defaultTo([], joinedData) as any}
            dataFetchKey={dataFetchKey}
            ref={metricsTableRef}
            filter={filter ? filter.filter : undefined}
            getDimensionCell={getDimensionCell}
            maxHeight={tableHeight}
            percentAOIPlaceholder={undefined}
            preset={comparisonPresetToUse}
            fixedWidth={tableWidth}
            kpiName={pickerKPIName}
            groupedSegments={groupedSegments}
            rowLabelPrefix={tildeRowPrefix}
            aggregateLabelPrefix={tilde}
            serverValues={tableServerValues}
          />
        )
      ) : (
        <MetricsTable
          columnMetaDataMap={{
            ...customSegmentColumnMap,
            ...columnMetaDataMap,
            ...dimensionColumnMetaDataMap
          }}
          data={R.defaultTo([], dataWithAllDims.data) as any}
          dataFetchKey={dataFetchKey}
          ref={metricsTableRef}
          filter={filter ? filter.filter : undefined}
          getDimensionCell={getDimensionCell}
          maxHeight={tableHeight}
          percentAOIPlaceholder={percentAOIPlaceholder}
          preset={processedTablePreset}
          fixedWidth={tableWidth}
          kpiName={pickerKPIName}
          groupedSegments={groupedSegments}
          rowLabelPrefix={tildeRowPrefix}
          aggregateLabelPrefix={tilde}
          serverValues={tableServerValues}
        />
      );
    }
    return undefined;
  }, [
    columnMetaDataMap,
    comparisonPresetToUse,
    customSegmentColumnMap,
    dataFetchKey,
    dataWithAllDims,
    dates,
    dimensionColumnMetaDataMap,
    filter,
    getDimensionCell,
    joinedData,
    percentAOIPlaceholder,
    pickerKPIName,
    prefix,
    processedTablePreset,
    rerenderTable,
    showMetricsTableComparison,
    tableHeight,
    tablePreset,
    tableServerValues,
    tableWidth,
    groupedSegments
  ]);

  const metricsTableBody = (
    <div className='performance metrics-table' id='performanceContainer' ref={containerRef}>
      {metricsTable || (
        <Skeleton>
          <TableSkeleton />
        </Skeleton>
      )}
    </div>
  );

  const filterBarLines = useMemo(
    () => (joinedData ?? []).map(line => line.dimensions),
    [joinedData]
  );

  const filterBarOptions = useMemo(
    () =>
      processedTablePreset && processedTablePreset.dimensionColumns
        ? R.uniq([
            ...R.map(col => col.dimensionTypeName, processedTablePreset.dimensionColumns),
            ...R.map(col => col.dimensionVarName, processedTablePreset.dimensionColumns)
          ])
        : [],
    [processedTablePreset]
  );

  const tableFilterBar = (
    <div className='filter-bar-container'>
      <FilterBar
        hasAdvanced
        onFilter={(newFilter, tokens) => {
          setFilter({ filter: newFilter });
          setTableFilterTerms(getBasicTableFilterTerms(tokens, filterBarOptions));
        }}
        lines={filterBarLines}
        options={filterBarOptions}
      />
    </div>
  );
  const duplicateTablePreset = useCallback(
    preset => {
      confirmSave(
        () => {
          (async () => {
            const fullyLoadedPreset =
              preset && !R.isNil(preset.id) && tablePresetMap[`${preset.id}`]
                ? tablePresetMap[`${preset.id}`]
                : await getTablePreset({ id: preset.id, company, mediatype: prefix });
            if (!tablePresetMap[`${preset.id}`] && fullyLoadedPreset) {
              setTablePresetMap(`${preset.id}`, fullyLoadedPreset);
            }

            const presetToSave = {
              ...fullyLoadedPreset,
              admin_lock: false
            } as MetricsTablePreset;
            saveTablePreset({
              company,
              id: 'new',
              mediatype: prefix,
              preset: presetToSave,
              name: `${preset.name} (Copy)`,
              temporary: R.defaultTo(false, (fullyLoadedPreset as MetricsTablePreset).temporary)
            });
          })();
        },
        { temporary: R.defaultTo(false, (preset as MetricsTablePreset).temporary) }
      );
    },
    [
      company,
      confirmSave,
      getTablePreset,
      prefix,
      saveTablePreset,
      setTablePresetMap,
      tablePresetMap
    ]
  );

  const tablePresetPicker = (
    <PresetDropdown
      analyticsIsSource={analyticsIsSource}
      presetType='table'
      size={Size.MD}
      addPresetOnClick={() => {
        setTablePresetChanges(
          changes =>
            ({ ...changes, id: 'new', name: 'My New Table Preset', admin_lock: false }) as any
        );
        setShowNewTablePresetModal(true);
      }}
      addPresetText='Add New Table Preset'
      applyOnClick={preset => {
        if (pagePreset) {
          setURL(pagePreset.name, preset.name);
        }
      }}
      cancelOnClick={() => {}}
      className={`tablePresetPicker ${fetchingTablePresets ? 'loading' : ''}`}
      defaultPresetNames={defaultTablePresetNames}
      deleteItemOnClick={presetID => deleteTablePreset({ id: presetID, mediatype: prefix })}
      duplicateItemOnClick={preset => duplicateTablePreset(preset)}
      editItemOnClick={preset => {
        (async () => {
          const fetchedTablePreset = (
            tablePresetMap[`${preset.id}`]
              ? tablePresetMap[`${preset.id}`]
              : await getTablePreset({
                  id: preset.id,
                  company,
                  mediatype: prefix
                })
          ) as MetricsTablePreset;
          if (!R.isNil(tablePresetMap[`${preset.id}`])) {
            setTablePresetMap(`${preset.id}`, fetchedTablePreset);
          }
          setTablePresetChanges(fetchedTablePreset);
          setShowEditTablePresetModal(true);
        })();
      }}
      hasTicks
      label={
        tablePreset && !R.isNil(tablePreset.id)
          ? `Table Preset: ${tablePreset.temporary ? 'Temp' : tablePreset.name}`
          : 'Load Table Preset'
      }
      presets={R.filter(
        preset => !preset.temporary || (!R.isNil(tablePreset) && preset.id === tablePreset.id),
        tablePresets
      )}
      renameItemOnClick={(preset: PresetIDGroups) => {
        setTablePresetChanges({ adminLock: preset.admin_lock, id: preset.id } as any);
        setShowRenameTablePresetModal(true);
      }}
      selectedPreset={tablePreset}
      otherPresetCols={activeFilterDims}
      channel={prefix}
    />
  );

  const glossaryButton = (
    <Button
      className='glossary-button'
      design={Design.NEUTRAL}
      leadingElement={<MdOutlineMenuBook />}
      onClick={() => setShowGlossary(true)}
    >
      Glossary
    </Button>
  );

  const downloadFullTableData = () => {
    const fullData = fullDataByKeyRef.current[fetchKey];
    if (!fullData?.data) {
      return;
    }
    const role = 10;
    const preset = processedTablePreset;
    if (!preset) {
      return;
    }
    const combinedColumnMetaDataMap = {
      ...R.defaultTo({}, customSegmentColumnMap),
      ...columnMetaDataMap,
      ...dimensionColumnMetaDataMap
    };
    const dataHeaders = getDataHeaders(preset, role);
    const dimensionDataHeaders = getDimensionDataHeaders(
      preset,
      role,
      groupedSegments,
      shouldEnableMetricsTableSegmentGroups
    );
    const numDimCols = dimensionDataHeaders.length;
    const numDataCols = dataHeaders.length;
    const sorting = preset.defaultSorting || [];
    const { dimensionData, tableData } = constructTableData(
      combinedColumnMetaDataMap,
      dataHeaders,
      dimensionDataHeaders,
      getDimensionCell || DEFAULT_GET_DIMENSION_CELL,
      dataFetchKey,
      // Same rows as the table: the in-table search, whether or not it reached the server.
      filter ? fullData.data.filter(row => filter.filter(row.dimensions)) : fullData.data,
      groupedSegments,
      shouldEnableMetricsTableSegmentGroups,
      {}
    );
    const { sortedDimensionData, sortedTableData } = sortData(
      dataHeaders,
      dimensionData,
      dimensionDataHeaders,
      sorting,
      tableData,
      true
    );
    const dataToExport = getExportedData({
      columnMetaDataMap: combinedColumnMetaDataMap,
      dataHeaders,
      dimensionDataHeaders,
      numDataCols,
      numDimCols,
      sortedDimensionData,
      sortedTableData
    });
    const csvContent = R.map(
      e =>
        e
          .map(cell => (typeof cell === 'number' ? cell : R.defaultTo('-', cell).replace(/,/g, '')))
          .join(','),
      dataToExport
    ).join('\n');
    let dateLabel = dates ? makePrettyDateRange(dates.start, dates.end) : '';
    dateLabel = dateLabel.replace(/\//g, '-');
    download(csvContent, `${prefix}_Metrics_Table_Kpi_${fetchKPI}_${dateLabel}.csv`, 'text/csv');
  };

  // Export the Metrics Table as CSV
  const exportTableData = () => {
    const csvContent = R.map(
      e =>
        e
          .map(cell => (typeof cell === 'number' ? cell : R.defaultTo('-', cell).replace(/,/g, '')))
          .join(','),
      metricsTableRef.current?.getExportedData() ?? []
    ).join('\n');

    let dateLabel = '';
    if (dates && showMetricsTableComparison && otherDates) {
      dateLabel = `primary_${makePrettyDateRange(
        dates.start,
        dates.end
      )}_comparison_${makePrettyDateRange(otherDates.start, otherDates.end)}`;
    } else if (dates) {
      dateLabel = makePrettyDateRange(dates.start, dates.end);
    }
    dateLabel = dateLabel.replace(/\//g, '-');
    const label = `${prefix}_Metrics_Table_Kpi_${fetchKPI}_${dateLabel}.csv`;
    download(csvContent, label, 'text/csv');
  };

  // Either download the Metrics Table as CSV or download Matched Responses
  const isStreamingSingleChannel = STREAMING_PLUS.includes(prefix);

  const downloadMenuOptions = ['CSV'];
  const downloadOnClickOptions = [metricsTableTruncated ? downloadFullTableData : exportTableData];

  if (isStreamingSingleChannel) {
    downloadMenuOptions.push('Matched Responses');
    downloadOnClickOptions.push(async () => {
      try {
        // If user opted to hide the modal, directly download; otherwise open modal
        if (localStorage.getItem(HIDE_MATCHED_RESPONSES_ALERT_KEY) === 'true') {
          await downloadMatchedResponses({
            kpi: fetchKPI,
            start: dates?.start,
            end: dates?.end,
            impsBetween: true,
            prefix: prefix as S.Prefix,
            isGraph: Boolean(isGraph),
            overloadFilenameKpi: fetchKPI,
            setError
          });
        } else {
          setShowMatchedResponsesModal(true);
        }
      } catch (e) {
        setError({
          reportError: e,
          message: e.message
        });
      }
    });
  }

  const downloadButton = (
    <DownloadDropdown menuOptions={downloadMenuOptions} onClickOptions={downloadOnClickOptions} />
  );

  const glossaryModal = (
    <GlossaryModal
      show={showGlossary}
      setShow={() => setShowGlossary(false)}
      title='Metrics Glossary'
      initialGlossary={glossary}
    />
  );

  const showComparisonButton = (
    <Button
      className='show-comparison-button'
      design={Design.NEUTRAL}
      onClick={() => {
        setShowMetricsTableComparison(!showMetricsTableComparison);
      }}
    >
      {showMetricsTableComparison ? 'Hide Comparison' : 'Show Comparison'}
    </Button>
  );

  const timeTravelButton = (
    <TimeTravelModal
      prefix={prefix}
      setBranchBuild={setBranchBuild}
      timeTravelOpen={timeTravelOpen}
      setTimeTravelOpen={setTimeTravelOpen}
    />
  );
  const metricsTableControls = mediaTypeEnabled && (
    <div className='metrics-table-controls-container'>
      {tableFilterBar}
      <div className='metrics-table-controls'>
        {(shouldEnableTimeTravel || isInternal) && isStreamingSingleChannel && timeTravelButton}
        {showComparisonButton}
        {glossaryButton}
        {tablePresetPicker}
        {downloadButton}
      </div>
    </div>
  );

  /// /////////////////////////
  // DELIVERY TAB
  /// /////////////////////////
  const delivery = deliveryData &&
    pagePresetChanges &&
    ((otherDates && otherDeliveryData) || !R.isNil(otherDates)) && (
      <Delivery
        company={company}
        data={deliveryData}
        dates={dates}
        defaultDateGroupingKey={
          pagePresetChanges && pagePresetChanges.dateInfo && pagePresetChanges.dateInfo.increments
            ? DATE_GROUPING_MAP[pagePresetChanges.dateInfo.increments]
            : undefined
        }
        defaultMetric={pagePresetChanges.globalOptions.deliveryMetric}
        deliveryMetricOptions={deliveryMetricOptions}
        dimension={deliveryDimension}
        deliveryDimensionMap={deliveryDimensionMap}
        getDimensionCell={getDimensionCell}
        otherData={otherDeliveryData}
        otherDates={otherDates}
        prefix={prefix}
        setDimension={setDeliveryDimension}
        overviewKeys={defaultDeliveryOverviewMetrics}
        incompatibleDeliveryDimsFromFilter={incompatibleDeliveryDimsFromFilter}
        filterID={deliveryFilter.filterID}
        filterState={deliveryFilter.filterState}
      />
    );

  let title: string;
  if (prefix === SEARCHSHOPPING) {
    title = 'Search & Shopping';
  } else if (prefix === YOUTUBE) {
    title = 'YouTube';
  } else {
    title = `${prefix.charAt(0).toUpperCase()}${prefix.substring(1)}`;
  }

  const performanceContextValue = useMemo(
    () => ({
      branchBuild,
      globalBrand,
      globalKpi: fetchKPI,
      globalLag: '7d' as const,
      impsBetween: R.isNil(impsBetween) ? true : impsBetween,
      isGraph,
      kpiMetaData,
      prefix: prefix === LINEAR ? ('tv' as const) : prefix
    }),
    [branchBuild, globalBrand, fetchKPI, impsBetween, isGraph, kpiMetaData, prefix]
  );

  const showLoadingOverlay =
    mediaTypeEnabled &&
    loading &&
    (selectedTab === TabKey.OVERVIEW || selectedTab === TabKey.DELIVERY);

  return (
    <PerformanceContext.Provider value={performanceContextValue}>
      <Page
        actions={actionsContainer}
        isLoading={loading}
        state={showLoadingOverlay ? PAGE_STATE.LOADING : undefined}
        minWidth='500px'
        navs={navsReady ? pageNavs : undefined}
        onNav={nav => {
          setSelectedTab(nav);
          if (selectedTab !== nav && nav === String(TabKey.METRICS_TABLE)) {
            setRerenderTable(true);
          }
          if (pagePresetName) {
            const navTablePresetName = tablePreset ? tablePreset.name : undefined;
            setURL(pagePresetName, navTablePresetName, nav);
          }
        }}
        pageType={title}
        selectedNav={selectedTab}
        subHeader={subHeader}
        title={<div className='performance-title'>{title}</div>}
      >
        {/* Modals */}
        {glossaryModal}
        {editPagePresetModal}
        {renamePagePresetModal}
        {editTablePresetModal}
        {renameTablePresetModal}
        {!mediaTypeEnabled ? (
          <div className='not-allowed'>This company does not support this media type.</div>
        ) : (
          <>
            {/* Overview */}
            {selectedTab === TabKey.OVERVIEW && overviewSparkCharts}
            <div className='overview-bottom-row'>
              {selectedTab === TabKey.OVERVIEW && overviewCompareMetricsChart}
              {selectedTab === TabKey.OVERVIEW &&
                [LINEAR, STREAMING_PLUS[0], STREAMING_PLUS[1]].includes(prefix) &&
                !loading &&
                overviewSparkCharts && (
                  <div className='optional-overview-bottom-chart'>
                    {selectedTab === TabKey.OVERVIEW && prefix === LINEAR && liftFromTvChart}
                    {selectedTab === TabKey.OVERVIEW &&
                      [STREAMING_PLUS[0], STREAMING_PLUS[1]].includes(prefix) &&
                      conversionLatencyChart}
                  </div>
                )}
            </div>
            {/* Metrics Table */}
            {selectedTab === TabKey.METRICS_TABLE && hasMetricsTableData && metricsTableControls}
            {selectedTab === TabKey.METRICS_TABLE && (
              <div className='metrics-table-and-footer'>
                {metricsTableBody}
                {metricsTableTruncated && !loading && (
                  <p ref={truncatedNoteRef} className='truncated-dataset-note'>
                    Showing the first {METRICS_TABLE_DISPLAY_CAP} rows. Use the download button
                    above for the complete dataset.
                  </p>
                )}
              </div>
            )}
            {/* Delivery */}
            {selectedTab === TabKey.DELIVERY && delivery}
            {/* Save */}
            {saving && <FullPageSpinner transparent fixed />}
            {/* Matched Responses */}
            {showMatchedResponsesModal && (
              <MatchedResponsesModal
                showMatchedResponsesModal={showMatchedResponsesModal}
                setShowMatchedResponsesModal={setShowMatchedResponsesModal}
                start={dates?.start}
                end={dates?.end}
              />
            )}
            <AlertDialog
              intent={Intent.SUCCESS}
              show={showLargeDatasetModal}
              onHide={() => setShowLargeDatasetModal(false)}
              icon={<MdOutlineWarning />}
              title='Dataset too large to display'
              body={`This preset returned more rows than can be displayed in-app. The table below shows the first ${METRICS_TABLE_DISPLAY_CAP} rows as a sample. Download the full dataset using the button below.`}
              primaryAction={{ label: 'Download Full Dataset', onClick: downloadFullTableData }}
              secondaryAction={{ label: 'Dismiss', onClick: () => setShowLargeDatasetModal(false) }}
            />
          </>
        )}
      </Page>
    </PerformanceContext.Provider>
  );
};
